import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import type { Collection, DatasetCatalogueEntry, GraphEdge, GraphNode, Locale, MythDataset, NodeType, Story, TimelineChunk, TimelineRoute, TimelineTarget } from './data/schema'
import {
  focusNodeIdFor,
  loadAtlasBootstrap,
  loadAtlasCollections,
  loadAtlasFocus,
  loadAtlasRegion,
  loadAtlasSources,
  loadAtlasStory,
  loadAtlasTimeline,
  mergeAtlasDataset,
  mergeAtlasEdges,
  regionsForNodeIds,
  type AtlasBootstrap,
  type AtlasCollectionSummary,
  type AtlasEdgeStub,
  type AtlasRegionChunk,
  type AtlasTopologyChunk,
} from './data/loader'
import type { AtlasLayout, AtlasRegionId } from './atlasLayout'
import { copy, localeName, localize, localizeWithEnglish } from './i18n'
import { filterGraph, getNeighborhood, getStoryOverviewGraph, NEIGHBOR_EXPANSION_BATCH, parseUrlState, resolveLocalePreference, serializeUrlState, type AppMode, type GraphLens, type UrlState } from './state'
import { FilterPanel, type RelationshipLabelMode } from './components/FilterPanel'
import { GraphCanvas, type GraphCanvasHandle } from './components/GraphCanvas'
import { normalizePresetPositions } from './storyOverviewLayout'
import { Icon } from './components/Icon'
import { Inspector, type InspectorSnap } from './components/Inspector'
import { SearchBox } from './components/SearchBox'
import { ShareMenu } from './components/ShareMenu'
import { StoryCatalog } from './components/StoryCatalog'
import { CollectionInspector } from './components/CollectionInspector'
import { NodeLegend } from './components/NodeLegend'
import { RelationshipLegend } from './components/RelationshipLegend'
import { AnalyticsConsentControl } from './components/AnalyticsConsent'
import { LanguageGate } from './components/LanguageGate'
import { TraditionSelector } from './components/TraditionSelector'
import { AtlasCanvas } from './components/AtlasCanvas'
import { TimelineControls, type TimelinePlaybackScope, type TimelineSpeed } from './components/TimelineControls'
import { trackEvent } from './analytics'
import { buildGraphShareFilename, createGraphShareCard, shareGraphCard } from './shareGraph'
import {
  buildAtlasFocusProjection,
  filterFocusedAtlasNodes,
  toAtlasSemanticEdge,
  type AtlasRelationshipBundle,
} from './atlasProjection'
import {
  findTimelineIntroduction,
  findTimelineScene,
  firstTimelineScene,
  flattenTimelineScenes,
  nextTimelineScene,
  resolveTimelinePosition,
  timelineCameraTargetsForScene,
  timelineDelayMs,
  timelinePositionForScene,
  timelineSceneForPosition,
  timelineSnapshot,
  type TimelineFlatScene,
  type TimelinePosition,
} from './timeline'
import {
  defaultTimelineNarrationModeForLocale,
  timelineNarrationDelayMs,
  timelineNarrationKey,
  timelineNarrationOptions,
  timelineNarrationTrackForScene,
  type TimelineNarrationMode,
} from './timelineNarration'
import { norseTimelineNarrationTrackForScene } from './norseTimelineNarration'
import { shouldPresentTraditionSelectorOnEntry } from './traditionEntry'

const allNodeTypes: NodeType[] = ['deity', 'hero', 'mortal', 'creature', 'place', 'event', 'artifact']
type NodeEntryPoint = 'graph' | 'search' | 'inspector'
type StoryNavigationMethod = 'direct' | 'story_open' | 'next' | 'previous' | 'progress'
type CollectionEntryPoint = 'search' | 'explorer'

interface ReferencePanelProps {
  locale: Locale
  collections: AtlasCollectionSummary[]
  onClose: () => void
  onOpenCollection: (id: string) => void
}

function ReferencePanel({ locale, collections, onClose, onOpenCollection }: ReferencePanelProps) {
  const availableCollections = collections.filter((collection) => collection.status === 'available')
  return <aside className="reference-panel" role="region" aria-label={locale === 'en' ? 'Reference lists' : '參考名單'}>
    <header>
      <div>
        <span className="eyebrow">REFERENCE LISTS · 參考名單</span>
        <h2>{locale === 'en' ? 'Reference lists' : '參考名單'}</h2>
      </div>
      <button type="button" className="icon-button" onClick={onClose} aria-label={locale === 'en' ? 'Close reference lists' : '關閉參考名單'}>
        <Icon name="close" />
      </button>
    </header>
    <div className="reference-list">
      {availableCollections.map((collection) => (
        <button key={collection.id} type="button" onClick={() => onOpenCollection(collection.id)}>
          <span className="reference-list-icon"><Icon name="list" size={18} /></span>
          <span>
            <strong>{localize(collection.title, locale)}</strong>
            <small>{locale === 'en'
              ? `${collection.memberCount} names · ${collection.variantCount} versions`
              : `${collection.memberCount} 個名字 · ${collection.variantCount} 個版本`}</small>
            {collection.description && <em>{localize(collection.description, locale)}</em>}
          </span>
          <Icon name="arrow-right" size={17} />
        </button>
      ))}
    </div>
  </aside>
}

const inactiveTimelineEras = [
  { order: 1, title: { 'zh-Hant': '混沌時代', en: 'Age of Chaos' } },
  { order: 2, title: { 'zh-Hant': '原始神時代', en: 'Primordial Gods' } },
  { order: 3, title: { 'zh-Hant': '泰坦時代', en: 'Age of Titans' } },
  { order: 4, title: { 'zh-Hant': '泰坦戰爭', en: 'Titanomachy' } },
  { order: 5, title: { 'zh-Hant': '奧林匹斯建立', en: 'Early Olympian Age' } },
  { order: 6, title: { 'zh-Hant': '英雄時代', en: 'Age of Heroes' } },
  { order: 7, title: { 'zh-Hant': '特洛伊時代', en: 'Trojan Cycle' } },
  { order: 8, title: { 'zh-Hant': '後英雄時代', en: 'Returns / Decline' } },
] as const

function TimelineInactiveControls({ locale, onActivate }: { locale: Locale; onActivate: () => void }) {
  return <section
    className="timeline-controls timeline-controls-light is-inactive is-shell"
    aria-label={locale === 'en' ? 'Timeline player' : '時間線播放器'}
    data-testid="timeline-controls"
  >
    <div className="timeline-player-main" aria-disabled="true">
      <div className="timeline-now-playing">
        <div className="timeline-player-kicker">
          <span className="eyebrow">{locale === 'en' ? '01 · Age of Chaos' : '01 · 混沌時代'}</span>
        </div>
        <strong>{locale === 'en' ? 'The First Powers' : '最初的力量 (The First Powers)'}</strong>
      </div>

      <div className="timeline-transport" aria-label={locale === 'en' ? 'Timeline transport' : '時間線播放控制'}>
        <button type="button" className="timeline-skip" disabled aria-label={locale === 'en' ? 'Previous scene' : '上一章節'}><Icon name="skip-back" /></button>
        <button type="button" className="timeline-play" disabled aria-label={locale === 'en' ? 'Play complete timeline' : '播放完整時間線'}>
          <Icon name="play" />
          <span>{locale === 'en' ? 'Play' : '播放'}</span>
        </button>
        <button type="button" className="timeline-skip" disabled aria-label={locale === 'en' ? 'Next scene' : '下一章節'}><Icon name="skip-forward" /></button>
      </div>

      <div className="timeline-scrubber">
        <label htmlFor="timeline-scene-inactive">
          <span>{locale === 'en' ? 'Timeline' : '時間線'}</span>
          <strong>0 / 0</strong>
        </label>
        <input id="timeline-scene-inactive" type="range" min={1} max={1} value={1} disabled readOnly aria-label={locale === 'en' ? 'Timeline inactive' : '時間線尚未啟用'} />
        <div className="timeline-progress-track"><i style={{ width: '0%' }} /></div>
      </div>

      <div className="timeline-player-actions">
        <button type="button" className="timeline-follow" disabled aria-label={locale === 'en' ? 'Follow map' : '跟隨地圖'}><Icon name="focus" /><span>{locale === 'en' ? 'Follow' : '跟隨'}</span></button>
        <button type="button" className="timeline-restart" disabled aria-label={locale === 'en' ? 'Restart timeline' : '重播時間線'}><Icon name="restart" /><span>{locale === 'en' ? 'Restart' : '重播'}</span></button>
        <button type="button" className="timeline-speed-static" disabled>1×</button>
        <button type="button" className="timeline-play-all" disabled title={locale === 'en' ? 'Open Timeline to play the full route' : '開啟時間線後可播放完整路線'}>
          <Icon name="play" size={14} />
          <span>{locale === 'en' ? 'Full route' : '完整路線'}</span>
        </button>
      </div>
    </div>

    <nav className="timeline-era-rail" aria-label={locale === 'en' ? 'Timeline ages' : '時間線階段'}>
      <button
        type="button"
        className="timeline-era-toggle"
        aria-pressed="false"
        onClick={onActivate}
        title={locale === 'en' ? 'Open Timeline' : '開啟時間線'}
      >
        <Icon name="spark" size={13} />
        <span>{locale === 'en' ? 'Timeline' : '時間線'}</span>
      </button>
      {inactiveTimelineEras.map((era) => <button key={era.order} type="button" disabled>
        <small>{String(era.order).padStart(2, '0')}</small>
        <span>{localize(era.title, locale)}</span>
      </button>)}
    </nav>
  </section>
}

function initialState(): UrlState {
  return parseUrlState(window.location.search, window.localStorage.getItem('mythweave-locale'), window.location.pathname)
}

function hasInitialLanguagePreference() {
  return resolveLocalePreference(window.location.search, window.localStorage.getItem('mythweave-locale'), window.location.pathname) !== null
}

function selectTimelineRoute(chunk: TimelineChunk | undefined, routeId?: string) {
  if (!chunk) return undefined
  return chunk.routes.find((item) => item.id === routeId) ?? chunk.routes[0]
}

async function loadInitialAtlasState(state: UrlState) {
  const bootstrap = await loadAtlasBootstrap(state.datasetId)
  const topologies: AtlasTopologyChunk[] = []
  const timeline = state.mode === 'explore' && bootstrap.atlas.timelinePath
    ? await loadAtlasTimeline(bootstrap.atlas)
    : undefined
  const collectionsRequest = state.collectionId ? loadAtlasCollections(bootstrap.atlas) : Promise.resolve([] as Collection[])
  if (state.mode === 'story' && state.storyId && bootstrap.atlas.stories.some((story) => story.id === state.storyId)) {
    topologies.push(await loadAtlasStory(bootstrap.atlas, state.storyId))
  }
  if (state.focusId) {
    const nodeId = focusNodeIdFor(bootstrap.atlas, state.focusId)
    if (bootstrap.atlas.nodes.some((node) => node.id === nodeId)) topologies.push(await loadAtlasFocus(bootstrap.atlas, nodeId))
  }
  if (!topologies.length) {
    const [sources, collections] = await Promise.all([
      state.collectionId ? loadAtlasSources(bootstrap.atlas) : Promise.resolve([] as MythDataset['sources']),
      collectionsRequest,
    ])
    const collectionNodeIds = collectionMemberNodeIds(collections, state.collectionId, state.collectionVariantId)
    const collectionRegionIds = regionsForNodeIds(bootstrap.atlas, collectionNodeIds)
    const regions = collectionRegionIds.length
      ? await Promise.all(collectionRegionIds.map((regionId) => loadAtlasRegion(bootstrap.atlas, regionId)))
      : []
    return {
      ...bootstrap,
      timeline,
      timelineHydrated: false,
      dataset: mergeAtlasDataset(bootstrap.dataset, { regions, sources, collections }),
      atlasEdges: mergeAtlasEdges([], regions.flatMap((region) => region.edges)),
      loadedRegionIds: new Set(collectionRegionIds),
    }
  }
  const nodeIds = [...new Set(topologies.flatMap((topology) => topology.nodeIds))]
  const regionIds = regionsForNodeIds(bootstrap.atlas, nodeIds)
  const [regions, sources, collections] = await Promise.all([
    Promise.all(regionIds.map((regionId) => loadAtlasRegion(bootstrap.atlas, regionId))),
    loadAtlasSources(bootstrap.atlas),
    collectionsRequest,
  ])
  return {
    ...bootstrap,
    timeline,
    timelineHydrated: false,
    dataset: mergeAtlasDataset(bootstrap.dataset, { regions, topologies, sources, collections }),
    atlasEdges: mergeAtlasEdges([], regions.flatMap((region) => region.edges)),
    loadedRegionIds: new Set(regionIds),
  }
}

function collectionVariantFor(collection: Collection | undefined, variantId: string | undefined) {
  if (!collection) return undefined
  return collection.variants.find((variant) => variant.id === variantId)
    ?? collection.variants.find((variant) => variant.id === collection.defaultVariantId)
    ?? collection.variants[0]
}

function collectionMemberNodeIds(collections: readonly Collection[], collectionId: string | undefined, variantId: string | undefined) {
  const collection = collections.find((item) => item.id === collectionId)
  const variant = collectionVariantFor(collection, variantId)
  return variant?.members.map((member) => member.nodeId) ?? []
}

function timelineNarrationTrackForDataset(
  datasetId: string | undefined,
  sceneId: string | undefined,
  mode: TimelineNarrationMode,
) {
  return datasetId === 'norse'
    ? norseTimelineNarrationTrackForScene(sceneId, mode)
    : timelineNarrationTrackForScene(sceneId, mode)
}

export function App() {
  const [dataset, setDataset] = useState<MythDataset | null>(null)
  const [datasetEntries, setDatasetEntries] = useState<DatasetCatalogueEntry[]>([])
  const [activeDatasetEntry, setActiveDatasetEntry] = useState<DatasetCatalogueEntry>()
  const [atlas, setAtlas] = useState<AtlasBootstrap | null>(null)
  const [atlasLayout, setAtlasLayout] = useState<AtlasLayout | null>(null)
  const [atlasEdges, setAtlasEdges] = useState<AtlasEdgeStub[]>([])
  const [loadedRegionIds, setLoadedRegionIds] = useState<Set<AtlasRegionId>>(new Set())
  const [loadingRegionIds, setLoadingRegionIds] = useState<Set<AtlasRegionId>>(new Set())
  const [detailLoading, setDetailLoading] = useState(false)
  const [timelineRoute, setTimelineRoute] = useState<TimelineRoute>()
  const [timelineHydrated, setTimelineHydrated] = useState(false)
  const [timelineLoading, setTimelineLoading] = useState(false)
  const [timelineError, setTimelineError] = useState('')
  const [timelinePlaying, setTimelinePlaying] = useState(false)
  const [timelineSpeed, setTimelineSpeed] = useState<TimelineSpeed>(1)
  const [timelinePlaybackScope, setTimelinePlaybackScope] = useState<TimelinePlaybackScope>('all')
  const [timelineFollowMap, setTimelineFollowMap] = useState(true)
  const [timelineNarrationMode, setTimelineNarrationMode] = useState<TimelineNarrationMode>(() => (
    defaultTimelineNarrationModeForLocale(initialState().locale)
  ))
  const [timelineNarrationDurations, setTimelineNarrationDurations] = useState<Record<string, number>>({})
  const [timelineNarrationCurrentTime, setTimelineNarrationCurrentTime] = useState(0)
  const [loadError, setLoadError] = useState('')
  const [urlState, setUrlState] = useState<UrlState>(initialState)
  const [languageChosen, setLanguageChosen] = useState(hasInitialLanguagePreference)
  const [traditionSelectorOpen, setTraditionSelectorOpen] = useState(() => shouldPresentTraditionSelectorOnEntry(window.location.search, window.location.pathname))
  const [traditionSelectorRequired, setTraditionSelectorRequired] = useState(() => shouldPresentTraditionSelectorOnEntry(window.location.search, window.location.pathname))
  const [storyCatalogOpen, setStoryCatalogOpen] = useState(false)
  const [referenceOpen, setReferenceOpen] = useState(false)
  const [collectionCameraRequest, setCollectionCameraRequest] = useState(0)
  const [welcomeDismissed, setWelcomeDismissed] = useState(() => {
    const state = initialState()
    return state.mode !== 'explore' || Boolean(state.focusId || state.timelineId || state.collectionId)
  })
  const [filterOpen, setFilterOpen] = useState(false)
  const [labelMode, setLabelMode] = useState<RelationshipLabelMode>(() => {
    const saved = window.localStorage.getItem('mythweave-edge-labels')
    if (saved === 'always' || saved === 'auto' || saved === 'focus') return saved
    return 'auto'
  })
  const [activeTypes, setActiveTypes] = useState<Set<NodeType>>(new Set(allNodeTypes))
  const [highlightedType, setHighlightedType] = useState<NodeType>()
  const [selectedBundle, setSelectedBundle] = useState<AtlasRelationshipBundle>()
  const [entryContextId, setEntryContextId] = useState<string>()
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  const [spoilerRevealed, setSpoilerRevealed] = useState(false)
  const [toast, setToast] = useState('')
  const [shareBusy, setShareBusy] = useState(false)
  const timelineNarrationAudioRef = useRef<HTMLAudioElement | null>(null)
  const [inspectorSnap, setInspectorSnap] = useState<InspectorSnap>(() => {
    const state = initialState()
    return state.mode === 'story' && !state.focusId ? 'half' : 'peek'
  })
  const [inspectorVisible, setInspectorVisible] = useState(() => {
    const state = initialState()
    return Boolean(state.focusId || state.mode === 'story' || state.timelineId || state.collectionId)
  })
  const [inspectorDragHeight, setInspectorDragHeight] = useState<number | null>(null)
  const [explanationMapEdgeId, setExplanationMapEdgeId] = useState<string>()
  const pendingStoryNavigationRef = useRef<StoryNavigationMethod>('direct')
  const atlasLoadSequenceRef = useRef(0)
  const lastTrackedStoryStepRef = useRef('')
  const viewedStoryStepsRef = useRef(new Map<string, Set<string>>())
  const completedStoriesRef = useRef(new Set<string>())
  const graphCanvasRef = useRef<GraphCanvasHandle>(null)
  const loadedRegionIdsRef = useRef(new Set<AtlasRegionId>())
  const pendingRegionLoadsRef = useRef(new Map<AtlasRegionId, Promise<AtlasRegionChunk>>())
  const detailLoadCountRef = useRef(0)

  const locale = urlState.locale
  const t = copy[locale]

  useEffect(() => {
    setTimelineNarrationMode((current) => (
      current === 'off' ? current : defaultTimelineNarrationModeForLocale(locale)
    ))
    setTimelineNarrationCurrentTime(0)
  }, [locale])

  const fetchData = useCallback((state: UrlState) => {
    const sequence = atlasLoadSequenceRef.current + 1
    atlasLoadSequenceRef.current = sequence
    setLoadError('')
    setDataset(null)
    setAtlas(null)
    setAtlasLayout(null)
    setAtlasEdges([])
    setLoadedRegionIds(new Set())
    setLoadingRegionIds(new Set())
    setTimelineRoute(undefined)
    setTimelineHydrated(false)
    setTimelineLoading(false)
    setTimelineError('')
    setTimelinePlaying(false)
    setSelectedBundle(undefined)
    setExplanationMapEdgeId(undefined)
    setExpandedIds(new Set())
    loadedRegionIdsRef.current = new Set()
    pendingRegionLoadsRef.current.clear()
    loadInitialAtlasState(state).then((result) => {
      if (sequence !== atlasLoadSequenceRef.current) return
      const route = selectTimelineRoute(result.timeline, state.timelineId)
      loadedRegionIdsRef.current = result.loadedRegionIds
      setDatasetEntries(result.catalogue.datasets)
      setActiveDatasetEntry(result.datasetEntry)
      setLoadedRegionIds(new Set(result.loadedRegionIds))
      setAtlas(result.atlas)
      setAtlasLayout(result.layout)
      setAtlasEdges(result.atlasEdges)
      setDataset(result.dataset)
      setTimelineRoute(route)
      setTimelineHydrated(Boolean(state.timelineId && route && result.timelineHydrated))
      if (result.datasetEntry.id !== state.datasetId) {
        setUrlState((current) => current.datasetId === state.datasetId ? { ...current, datasetId: result.datasetEntry.id } : current)
      }
    }).catch((error: unknown) => {
      if (sequence !== atlasLoadSequenceRef.current) return
      setLoadError(error instanceof Error ? error.message : String(error))
    })
  }, [])

  useEffect(() => fetchData(urlState), [fetchData, urlState.datasetId])

  const requestRegionChunk = useCallback((regionId: AtlasRegionId) => {
    if (!atlas) return Promise.reject(new Error(`Atlas bootstrap is not ready for ${regionId}`))
    if (loadedRegionIdsRef.current.has(regionId)) return loadAtlasRegion(atlas, regionId)
    const pending = pendingRegionLoadsRef.current.get(regionId)
    if (pending) return pending
    setLoadingRegionIds((ids) => new Set([...ids, regionId]))
    const request = loadAtlasRegion(atlas, regionId)
      .catch((error: unknown) => {
        setToast(locale === 'en' ? 'Map detail could not be loaded.' : '地圖細節載入失敗。')
        window.setTimeout(() => setToast(''), 2600)
        throw error
      })
      .finally(() => {
        pendingRegionLoadsRef.current.delete(regionId)
        setLoadingRegionIds((ids) => {
          const next = new Set(ids)
          next.delete(regionId)
          return next
        })
      })
    pendingRegionLoadsRef.current.set(regionId, request)
    return request
  }, [atlas, locale])

  const commitRegionChunks = useCallback((regions: AtlasRegionChunk[]) => {
    regions.forEach((region) => loadedRegionIdsRef.current.add(region.regionId))
    setLoadedRegionIds(new Set(loadedRegionIdsRef.current))
    setAtlasEdges((current) => mergeAtlasEdges(current, regions.flatMap((region) => region.edges)))
    setDataset((current) => current ? mergeAtlasDataset(current, { regions }) : current)
  }, [])

  const ensureRegion = useCallback(async (regionId: AtlasRegionId) => {
    const chunk = await requestRegionChunk(regionId)
    commitRegionChunks([chunk])
    return chunk
  }, [commitRegionChunks, requestRegionChunk])

  const hydrateTopology = useCallback(async (topologyRequest: Promise<AtlasTopologyChunk>) => {
    if (!atlas) throw new Error('Atlas bootstrap is not ready')
    const topology = await topologyRequest
    const regionIds = regionsForNodeIds(atlas, topology.nodeIds)
    const [regions, sources] = await Promise.all([
      Promise.all(regionIds.map(requestRegionChunk)),
      loadAtlasSources(atlas),
    ])
    regions.forEach((region) => loadedRegionIdsRef.current.add(region.regionId))
    setLoadedRegionIds(new Set(loadedRegionIdsRef.current))
    setAtlasEdges((current) => mergeAtlasEdges(current, regions.flatMap((region) => region.edges)))
    setDataset((current) => current ? mergeAtlasDataset(current, { regions, topologies: [topology], sources }) : current)
  }, [atlas, requestRegionChunk])

  const ensureFocusData = useCallback(async (focusId: string) => {
    if (!atlas) throw new Error('Atlas bootstrap is not ready')
    const nodeId = focusNodeIdFor(atlas, focusId)
    await hydrateTopology(loadAtlasFocus(atlas, nodeId))
  }, [atlas, hydrateTopology])

  const ensureStoryData = useCallback(async (storyId: string) => {
    if (!atlas) throw new Error('Atlas bootstrap is not ready')
    await hydrateTopology(loadAtlasStory(atlas, storyId))
  }, [atlas, hydrateTopology])

  const ensureCollectionData = useCallback(async (collectionId?: string, variantId?: string) => {
    if (!atlas) throw new Error('Atlas bootstrap is not ready')
    const [collections, sources] = await Promise.all([
      loadAtlasCollections(atlas),
      loadAtlasSources(atlas),
    ])
    const collectionNodeIds = collectionMemberNodeIds(collections, collectionId, variantId)
    const collectionRegionIds = regionsForNodeIds(atlas, collectionNodeIds)
    const missingRegionIds = collectionRegionIds.filter((regionId) => !loadedRegionIdsRef.current.has(regionId))
    const regions = missingRegionIds.length
      ? await Promise.all(missingRegionIds.map(requestRegionChunk))
      : []
    if (regions.length) {
      regions.forEach((region) => loadedRegionIdsRef.current.add(region.regionId))
      setLoadedRegionIds(new Set(loadedRegionIdsRef.current))
      setAtlasEdges((current) => mergeAtlasEdges(current, regions.flatMap((region) => region.edges)))
    }
    setDataset((current) => current ? mergeAtlasDataset(current, { regions, collections, sources }) : current)
    return collections
  }, [atlas, requestRegionChunk])

  const ensureTimelineData = useCallback(async () => {
    if (!atlas) throw new Error('Atlas bootstrap is not ready')
    if (!atlas.timelinePath) {
      const message = locale === 'en'
        ? 'This mythology tradition does not have a public timeline yet.'
        : '這個神話傳統暫時未有公開時間線。'
      setTimelineError(message)
      setToast(message)
      window.setTimeout(() => setToast(''), 2600)
      throw new Error(message)
    }
    setTimelineLoading(true)
    setTimelineError('')
    setTimelinePlaying(false)
    setTimelineHydrated(false)
    try {
      const chunk = timelineRoute ? undefined : await loadAtlasTimeline(atlas)
      const route = timelineRoute ?? selectTimelineRoute(chunk, urlState.timelineId)
      if (!route) {
        throw new Error(locale === 'en' ? 'The timeline route is unavailable.' : '時間線路線無法載入。')
      }
      const storyIds = [...new Set(route.eras.flatMap((era) => era.subEras.flatMap((subEra) => (
        subEra.ticks.map((tick) => tick.storyId).filter((id): id is string => Boolean(id))
      ))))]
      const topologies = await Promise.all(storyIds.map((storyId) => loadAtlasStory(atlas, storyId)))
      const regionIds = regionsForNodeIds(atlas, [...new Set(topologies.flatMap((topology) => topology.nodeIds))])
      const [regions, sources] = await Promise.all([
        Promise.all(regionIds.map(requestRegionChunk)),
        loadAtlasSources(atlas),
      ])
      regions.forEach((region) => loadedRegionIdsRef.current.add(region.regionId))
      setLoadedRegionIds(new Set(loadedRegionIdsRef.current))
      setAtlasEdges((current) => mergeAtlasEdges(current, regions.flatMap((region) => region.edges)))
      setDataset((current) => current ? mergeAtlasDataset(current, { regions, topologies, sources }) : current)
      setTimelineRoute(route)
      setTimelineHydrated(true)
      return route
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      setTimelineError(message)
      throw error
    } finally {
      setTimelineLoading(false)
    }
  }, [atlas, locale, requestRegionChunk, timelineRoute, urlState.timelineId])

  const runDetailLoad = useCallback(async (task: () => Promise<void>) => {
    detailLoadCountRef.current += 1
    setDetailLoading(true)
    // Let the immediate loading state paint before topology parsing and graph hydration
    // contend for the main thread after a node, edge, or story interaction.
    await new Promise<void>((resolve) => {
      window.requestAnimationFrame(() => window.setTimeout(resolve, 0))
    })
    try { await task() }
    catch (error) {
      console.error('Atlas detail loading failed', error)
      setToast(locale === 'en' ? 'This part of the myth atlas could not be loaded.' : '這部分神話地圖載入失敗。')
      window.setTimeout(() => setToast(''), 2600)
      throw error
    } finally {
      detailLoadCountRef.current -= 1
      if (detailLoadCountRef.current === 0) setDetailLoading(false)
    }
  }, [locale])

  useEffect(() => {
    document.documentElement.lang = languageChosen ? locale : 'en'
    if (!languageChosen) return
    window.localStorage.setItem('mythweave-locale', locale)
    const nextUrl = `${window.location.pathname}${serializeUrlState(urlState)}`
    const currentUrl = `${window.location.pathname}${window.location.search}`
    if (nextUrl !== currentUrl) {
      if (timelinePlaying) window.history.replaceState(null, '', nextUrl)
      else window.history.pushState(null, '', nextUrl)
    }
  }, [urlState, locale, languageChosen, timelinePlaying])
  useEffect(() => {
    const restoreFromHistory = () => {
      const restored = parseUrlState(window.location.search, window.localStorage.getItem('mythweave-locale'), window.location.pathname)
      setUrlState(restored)
      setWelcomeDismissed(restored.mode !== 'explore' || Boolean(restored.focusId || restored.timelineId || restored.collectionId))
      setInspectorVisible(Boolean(restored.focusId || restored.mode === 'story' || restored.timelineId || restored.collectionId))
      setTimelinePlaying(false)
      setExplanationMapEdgeId(undefined)
      setExpandedIds(new Set())
      setInspectorSnap(restored.mode === 'story' && !restored.focusId ? 'half' : 'peek')
    }
    window.addEventListener('popstate', restoreFromHistory)
    return () => window.removeEventListener('popstate', restoreFromHistory)
  }, [])
  useEffect(() => window.localStorage.setItem('mythweave-edge-labels', labelMode), [labelMode])

  const isTimelineRequested = urlState.mode === 'explore' && Boolean(atlas?.timelinePath) && Boolean(urlState.timelineId)
  useEffect(() => {
    if (!isTimelineRequested || timelineHydrated || timelineLoading || timelineError) return
    void ensureTimelineData().catch(() => undefined)
  }, [ensureTimelineData, isTimelineRequested, timelineError, timelineHydrated, timelineLoading])

  useEffect(() => {
    if (!urlState.collectionId || dataset?.collections.some((collection) => collection.id === urlState.collectionId)) return
    void ensureCollectionData(urlState.collectionId, urlState.collectionVariantId).catch(() => undefined)
  }, [dataset?.collections, ensureCollectionData, urlState.collectionId, urlState.collectionVariantId])

  useEffect(() => {
    if (!isTimelineRequested || !timelineRoute) return
    const legacyPosition = resolveTimelinePosition(timelineRoute, {
      eraId: urlState.eraId,
      subEraId: urlState.subEraId,
      tick: urlState.timelineTick ?? 0,
    })
    const resolvedScene = urlState.timelineSceneId
      ? findTimelineScene(timelineRoute, urlState.timelineSceneId)
      : timelineSceneForPosition(timelineRoute, legacyPosition)
    if (!resolvedScene) return
    const resolved = timelinePositionForScene(timelineRoute, resolvedScene.scene.id)
    if (resolvedScene.scene.id === urlState.timelineSceneId
      && resolved.eraId === urlState.eraId
      && resolved.subEraId === urlState.subEraId
      && resolved.tick === (urlState.timelineTick ?? 0)) return
    setUrlState((state) => ({
      ...state,
      timelineId: timelineRoute.id,
      eraId: resolved.eraId,
      subEraId: resolved.subEraId,
      timelineSceneId: resolvedScene.scene.id,
      timelineTick: resolved.tick,
    }))
  }, [isTimelineRequested, timelineRoute, urlState.eraId, urlState.subEraId, urlState.timelineSceneId, urlState.timelineTick])

  useEffect(() => {
    const pauseInBackground = () => {
      if (document.hidden) setTimelinePlaying(false)
    }
    document.addEventListener('visibilitychange', pauseInBackground)
    return () => document.removeEventListener('visibilitychange', pauseInBackground)
  }, [])

  const timelineScenePlaybackDelayMs = useCallback((sceneId: string | undefined) => {
    const narrationTrack = timelineNarrationTrackForDataset(urlState.datasetId, sceneId, timelineNarrationMode)
    if (!narrationTrack || !sceneId) return timelineDelayMs(timelineSpeed)
    const key = timelineNarrationKey(sceneId, narrationTrack.id)
    return timelineNarrationDelayMs(narrationTrack, timelineSpeed, timelineNarrationDurations[key])
  }, [timelineNarrationDurations, timelineNarrationMode, timelineSpeed, urlState.datasetId])

  const advanceTimelinePlayback = useCallback(() => {
    if (!timelineRoute) return
    const current = findTimelineScene(timelineRoute, urlState.timelineSceneId)
    const next = nextTimelineScene(timelineRoute, current?.scene.id, timelinePlaybackScope)
    if (!next) {
      setTimelinePlaying(false)
      return
    }
    const position = timelinePositionForScene(timelineRoute, next.scene.id)
    setTimelineNarrationCurrentTime(0)
    setUrlState((state) => ({
      ...state,
      eraId: position.eraId,
      subEraId: position.subEraId,
      timelineSceneId: next.scene.id,
      timelineTick: position.tick,
      focusId: undefined,
    }))
  }, [timelinePlaybackScope, timelineRoute, urlState.timelineSceneId])

  useEffect(() => {
    if (!timelinePlaying || !timelineRoute || !isTimelineRequested || timelineLoading || timelineError) return
    if (timelineNarrationTrackForDataset(urlState.datasetId, urlState.timelineSceneId, timelineNarrationMode)) return
    const timer = window.setTimeout(advanceTimelinePlayback, timelineScenePlaybackDelayMs(urlState.timelineSceneId))
    return () => window.clearTimeout(timer)
  }, [
    advanceTimelinePlayback,
    isTimelineRequested,
    timelineError,
    timelineLoading,
    timelinePlaying,
    timelineRoute,
    timelineNarrationMode,
    urlState.datasetId,
    timelineScenePlaybackDelayMs,
    urlState.timelineSceneId,
  ])

  const currentStory = useMemo(() => dataset?.stories.find((story) => story.id === urlState.storyId), [dataset, urlState.storyId])
  const stepIndex = currentStory ? Math.min(Math.max(urlState.step - 1, 0), Math.max(currentStory.steps.length - 1, 0)) : 0
  const isStoryOverview = urlState.mode === 'story' && urlState.storyView === 'overview'
  const storyStep = isStoryOverview ? undefined : currentStory?.steps[stepIndex]
  const selectedEdge = urlState.focusId ? dataset?.edges.find((edge) => edge.id === urlState.focusId) : undefined
  const selectedNode = urlState.focusId && !selectedEdge ? dataset?.nodes.find((node) => node.id === urlState.focusId) : undefined
  const selectedCollection = useMemo(() => dataset?.collections.find((collection) => collection.id === urlState.collectionId), [dataset, urlState.collectionId])
  const selectedCollectionVariant = useMemo(() => collectionVariantFor(selectedCollection, urlState.collectionVariantId), [selectedCollection, urlState.collectionVariantId])
  const availableCollectionSummaries = useMemo(() => atlas?.collections.filter((collection) => collection.status === 'available') ?? [], [atlas])
  const activeDatasetName = activeDatasetEntry ? localize(activeDatasetEntry.name, locale) : locale === 'en' ? 'Greek Mythology' : '希臘神話'
  const activeDatasetShortName = activeDatasetEntry ? localize(activeDatasetEntry.shortName, locale) : activeDatasetName
  const activeDatasetSlug = activeDatasetEntry?.id === 'greek' || !activeDatasetEntry ? '' : `${activeDatasetEntry.slug}/`
  const storyPreviewEnabled = activeDatasetEntry?.id === 'norse'
  const canOpenStoryRecord = useCallback((story?: Story) => Boolean(story && (
    story.status === 'available' || (storyPreviewEnabled && story.steps.length > 0)
  )), [storyPreviewEnabled])
  const localePathSegment = locale === 'zh-Hant' ? 'zh-hant' : 'en'
  const publicLibraryHref = activeDatasetEntry?.id === 'greek' || !activeDatasetEntry
    ? `/${localePathSegment}/library/`
    : `/${localePathSegment}/${activeDatasetEntry.slug}/`
  const brandSymbol = 'M'
  const brandSubtitle = activeDatasetEntry?.id === 'norse'
    ? locale === 'en' ? 'NORSE ATLAS' : '北歐神話地圖'
    : 'THE HELLENIC ATLAS'
  const collectionHighlightIds = useMemo(() => (
    selectedCollectionVariant ? new Set(selectedCollectionVariant.members.map((member) => member.nodeId)) : undefined
  ), [selectedCollectionVariant])
  const entryContextNode = entryContextId ? dataset?.nodes.find((node) => node.id === entryContextId) : undefined
  const activeTimelineScene = useMemo<TimelineFlatScene | undefined>(() => {
    if (!isTimelineRequested || !timelineRoute) return undefined
    return findTimelineScene(timelineRoute, urlState.timelineSceneId)
  }, [isTimelineRequested, timelineRoute, urlState.timelineSceneId])
  const timelineDockScene = useMemo<TimelineFlatScene | undefined>(() => {
    if (!timelineRoute) return undefined
    if (isTimelineRequested) return findTimelineScene(timelineRoute, urlState.timelineSceneId)
    return firstTimelineScene(timelineRoute)
  }, [isTimelineRequested, timelineRoute, urlState.timelineSceneId])
  const activeTimelineNarrationTrack = useMemo(() => (
    timelineNarrationTrackForDataset(urlState.datasetId, activeTimelineScene?.scene.id, timelineNarrationMode)
  ), [activeTimelineScene?.scene.id, timelineNarrationMode, urlState.datasetId])
  const activeTimelineNarrationKey = activeTimelineNarrationTrack && activeTimelineScene
    ? timelineNarrationKey(activeTimelineScene.scene.id, activeTimelineNarrationTrack.id)
    : ''
  const timelineDockSceneDurationMs = useMemo(() => (
    timelineScenePlaybackDelayMs(timelineDockScene?.scene.id)
  ), [timelineDockScene?.scene.id, timelineScenePlaybackDelayMs])
  const timelineDurationMsBySceneId = useMemo(() => {
    if (!timelineRoute) return undefined
    const durations: Record<string, number> = {}
    flattenTimelineScenes(timelineRoute).forEach((item) => {
      durations[item.scene.id] = timelineScenePlaybackDelayMs(item.scene.id)
    })
    return durations
  }, [timelineRoute, timelineScenePlaybackDelayMs])

  useEffect(() => {
    const audio = timelineNarrationAudioRef.current
    if (!audio) return
    if (!activeTimelineNarrationTrack || !activeTimelineNarrationKey || !isTimelineRequested || timelineLoading || timelineError) {
      audio.pause()
      audio.removeAttribute('src')
      audio.dataset.narrationKey = ''
      setTimelineNarrationCurrentTime(0)
      return
    }

    const nextSrc = new URL(`${import.meta.env.BASE_URL}${activeTimelineNarrationTrack.src}`, window.location.href).href
    if (audio.dataset.narrationKey !== activeTimelineNarrationKey || audio.src !== nextSrc) {
      audio.pause()
      audio.src = nextSrc
      audio.currentTime = 0
      audio.dataset.narrationKey = activeTimelineNarrationKey
      setTimelineNarrationCurrentTime(0)
      audio.load()
    }
    audio.playbackRate = timelineSpeed

    if (!timelinePlaying) {
      audio.pause()
      return
    }

    void audio.play().catch(() => {
      setTimelinePlaying(false)
      setToast(locale === 'en'
        ? 'Narration audio could not start. Playback is paused.'
        : '旁白音訊未能開始播放，時間線已暫停。')
      window.setTimeout(() => setToast(''), 2600)
    })
  }, [
    activeTimelineNarrationKey,
    activeTimelineNarrationTrack,
    isTimelineRequested,
    locale,
    timelineError,
    timelineLoading,
    timelinePlaying,
    timelineSpeed,
  ])

  const handleTimelineNarrationMetadata = useCallback(() => {
    const audio = timelineNarrationAudioRef.current
    if (!audio || !activeTimelineNarrationKey || !Number.isFinite(audio.duration) || audio.duration <= 0) return
    const duration = Number(audio.duration.toFixed(2))
    setTimelineNarrationCurrentTime(Number(audio.currentTime.toFixed(2)))
    setTimelineNarrationDurations((current) => (
      current[activeTimelineNarrationKey] === duration
        ? current
        : { ...current, [activeTimelineNarrationKey]: duration }
    ))
  }, [activeTimelineNarrationKey])

  const handleTimelineNarrationTimeUpdate = useCallback(() => {
    const audio = timelineNarrationAudioRef.current
    if (!audio || !activeTimelineNarrationKey || !Number.isFinite(audio.currentTime)) return
    setTimelineNarrationCurrentTime(Number(audio.currentTime.toFixed(2)))
  }, [activeTimelineNarrationKey])

  const seekTimelineNarration = useCallback((timeSeconds: number) => {
    const audio = timelineNarrationAudioRef.current
    if (!audio || !activeTimelineNarrationKey || !activeTimelineNarrationTrack) return
    const duration = Number.isFinite(audio.duration) && audio.duration > 0
      ? audio.duration
      : timelineNarrationDurations[activeTimelineNarrationKey]
    if (!Number.isFinite(duration) || duration <= 0) return
    const nextTime = Math.min(Math.max(0, timeSeconds), duration)
    audio.currentTime = nextTime
    setTimelineNarrationCurrentTime(Number(nextTime.toFixed(2)))
  }, [activeTimelineNarrationKey, activeTimelineNarrationTrack, timelineNarrationDurations])

  const handleTimelineNarrationEnded = useCallback(() => {
    if (!timelinePlaying || !activeTimelineNarrationTrack) return
    advanceTimelinePlayback()
  }, [activeTimelineNarrationTrack, advanceTimelinePlayback, timelinePlaying])

  const handleTimelineNarrationError = useCallback(() => {
    if (!activeTimelineNarrationTrack || !isTimelineRequested) return
    setTimelinePlaying(false)
    setToast(locale === 'en'
      ? 'Narration audio is unavailable for this chapter.'
      : '此章節旁白音訊未能載入。')
    window.setTimeout(() => setToast(''), 2600)
  }, [activeTimelineNarrationTrack, isTimelineRequested, locale])

  const timelinePosition = useMemo<TimelinePosition | undefined>(() => {
    if (!isTimelineRequested || !timelineRoute) return undefined
    return activeTimelineScene
      ? timelinePositionForScene(timelineRoute, activeTimelineScene.scene.id)
      : resolveTimelinePosition(timelineRoute, {
        eraId: urlState.eraId,
        subEraId: urlState.subEraId,
        tick: urlState.timelineTick ?? 0,
      })
  }, [activeTimelineScene, isTimelineRequested, timelineRoute, urlState.eraId, urlState.subEraId, urlState.timelineTick])
  const activeTimelineSnapshot = useMemo(() => (
    timelineRoute && timelinePosition ? timelineSnapshot(timelineRoute, timelinePosition) : undefined
  ), [timelinePosition, timelineRoute])
  const activeTimelineEra = timelineRoute && timelinePosition
    ? timelineRoute.eras.find((item) => item.id === timelinePosition.eraId)
    : undefined
  const activeTimelineSubEra = activeTimelineEra && timelinePosition
    ? activeTimelineEra.subEras.find((item) => item.id === timelinePosition.subEraId)
    : undefined
  const activeTimelineTarget = activeTimelineScene?.scene.primaryFocus
  const activeTimelineTargetId = activeTimelineTarget?.kind === 'node'
    ? activeTimelineTarget.nodeId
    : activeTimelineTarget?.edgeId
  const activeTimelineCameraTargets = useMemo<readonly TimelineTarget[] | undefined>(() => {
    if (!dataset || !timelineHydrated || !isTimelineRequested || !activeTimelineScene || !activeTimelineSubEra || urlState.focusId) return undefined
    return timelineCameraTargetsForScene(activeTimelineScene.scene, activeTimelineSubEra, dataset)
  }, [activeTimelineScene, activeTimelineSubEra, dataset, isTimelineRequested, timelineHydrated, urlState.focusId])
  const timelineDimmedNodeIds = useMemo(() => {
    if (!dataset || !activeTimelineSnapshot) return undefined
    const currentIds = new Set(activeTimelineSnapshot.currentSubEraNodeIds)
    dataset.edges.forEach((edge) => {
      if (!activeTimelineSnapshot.currentSubEraEdgeIds.has(edge.id)) return
      currentIds.add(edge.source)
      currentIds.add(edge.target)
    })
    return new Set([...activeTimelineSnapshot.revealedNodeIds].filter((id) => !currentIds.has(id)))
  }, [activeTimelineSnapshot, dataset])
  const atlasBaseFocusId = selectedNode?.id ?? selectedEdge?.source
  const atlasProjection = useMemo(() => {
    if (dataset && activeTimelineSnapshot) {
      const visibleNodes = dataset.nodes.filter((node) => activeTimelineSnapshot.revealedNodeIds.has(node.id))
      const visibleNodeIds = new Set(visibleNodes.map((node) => node.id))
      const visibleEdges = dataset.edges
        .filter((edge) => activeTimelineSnapshot.currentSubEraEdgeIds.has(edge.id)
          && visibleNodeIds.has(edge.source)
          && visibleNodeIds.has(edge.target))
        .map(toAtlasSemanticEdge)
      return { nodes: visibleNodes, edges: visibleEdges }
    }
    if (!dataset || !atlasBaseFocusId) return { nodes: dataset?.nodes ?? [], edges: atlasEdges }
    const projectedEdges = buildAtlasFocusProjection(dataset, atlasBaseFocusId, {
      currentStoryId: urlState.mode === 'story' ? currentStory?.id : undefined,
      selectedEdgeId: selectedEdge?.id,
    })
    const preservedNodeIds = new Set<string>()
    if (selectedEdge) {
      preservedNodeIds.add(selectedEdge.source)
      preservedNodeIds.add(selectedEdge.target)
    }
    return filterFocusedAtlasNodes(
      dataset.nodes,
      projectedEdges,
      atlasBaseFocusId,
      highlightedType,
      preservedNodeIds,
    )
  }, [activeTimelineSnapshot, atlasBaseFocusId, atlasEdges, currentStory?.id, dataset, highlightedType, selectedEdge, urlState.mode])
  const selectedEdgeContext = useMemo(() => {
    if (!selectedEdge) return undefined
    const stepId = storyStep?.id
    return selectedEdge.contexts.find((context) => Boolean(
      currentStory
      && context.storyIds.includes(currentStory.id)
      && (!stepId || context.stepIds.includes(stepId)),
    ))
      ?? selectedEdge.contexts.find((context) => Boolean(currentStory && context.storyIds.includes(currentStory.id)))
      ?? selectedEdge.contexts.find((context) => context.relationshipClass && context.explanationDecision)
  }, [currentStory, selectedEdge, storyStep?.id])

  useEffect(() => {
    if (explanationMapEdgeId && explanationMapEdgeId !== selectedEdge?.id) setExplanationMapEdgeId(undefined)
  }, [explanationMapEdgeId, selectedEdge?.id])

  useEffect(() => {
    const localePath = locale === 'zh-Hant' ? 'zh-hant' : 'en'
    const siteUrl = 'https://mythweave.cc'
    const defaultDescription = activeDatasetEntry?.description[locale] ?? (locale === 'en'
      ? 'Follow the threads connecting gods, heroes, creatures, places and stories.'
      : 'MythWeave 是一張中英雙語互動神話地圖，可探索人物、事件和故事之間的關係。')
    const title = selectedNode
      ? locale === 'en' ? `${selectedNode.name.en} in ${activeDatasetName} | MythWeave` : `${selectedNode.name['zh-Hant']}：${activeDatasetName}圖鑑 | MythWeave`
      : currentStory
        ? locale === 'en' ? `${currentStory.title.en}: ${activeDatasetName} Story | MythWeave` : `${currentStory.title['zh-Hant']}：${activeDatasetName}故事 | MythWeave`
        : selectedCollection
          ? locale === 'en' ? `${selectedCollection.title.en}: ${activeDatasetName} Reference List | MythWeave` : `${selectedCollection.title['zh-Hant']}：${activeDatasetName}參考名單 | MythWeave`
        : `MythWeave | ${activeDatasetName} Atlas`
    const description = selectedNode?.summary[locale] ?? currentStory?.description[locale] ?? selectedCollection?.description[locale] ?? defaultDescription
    const canonical = selectedNode
      ? `${siteUrl}/${localePath}/${activeDatasetSlug}atlas/${selectedNode.id}/`
      : currentStory
        ? `${siteUrl}/${localePath}/${activeDatasetSlug}stories/${currentStory.id}/`
        : selectedCollection
          ? `${siteUrl}/${localePath}/${activeDatasetSlug}?collection=${selectedCollection.id}`
        : `${siteUrl}/${localePath}/${activeDatasetSlug}`
    const image = selectedNode?.mediaSet?.inspectorHero?.src ?? selectedNode?.media?.src

    document.title = title
    const setMeta = (selector: string, content: string) => document.querySelector<HTMLMetaElement>(selector)?.setAttribute('content', content)
    setMeta('meta[name="description"]', description)
    setMeta('meta[property="og:title"]', title)
    setMeta('meta[property="og:description"]', description)
    setMeta('meta[property="og:url"]', canonical)
    setMeta('meta[property="og:locale"]', locale === 'en' ? 'en_US' : 'zh_HK')
    setMeta('meta[name="twitter:title"]', title)
    setMeta('meta[name="twitter:description"]', description)
    document.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.setAttribute('href', canonical)

    let imageMeta = document.querySelector<HTMLMetaElement>('meta[property="og:image"][data-runtime-seo]')
    if (image) {
      if (!imageMeta) {
        imageMeta = document.createElement('meta')
        imageMeta.setAttribute('property', 'og:image')
        imageMeta.dataset.runtimeSeo = 'true'
        document.head.appendChild(imageMeta)
      }
      imageMeta.content = `${siteUrl}/${image.replace(/^\//, '')}`
      setMeta('meta[name="twitter:card"]', 'summary_large_image')
    } else {
      imageMeta?.remove()
      setMeta('meta[name="twitter:card"]', 'summary')
    }
  }, [activeDatasetEntry, activeDatasetName, activeDatasetSlug, currentStory, locale, selectedCollection, selectedNode])

  useEffect(() => {
    if (urlState.mode !== 'story' || !currentStory || isStoryOverview) {
      lastTrackedStoryStepRef.current = ''
      pendingStoryNavigationRef.current = 'direct'
      return
    }
    const step = storyStep
    if (!step) return
    const stepKey = `${currentStory.id}:${step.id}`
    if (lastTrackedStoryStepRef.current === stepKey) return

    const navigationMethod = pendingStoryNavigationRef.current
    lastTrackedStoryStepRef.current = stepKey
    pendingStoryNavigationRef.current = 'direct'
    const trackedStep = trackEvent('story_step_view', {
      story_id: currentStory.id,
      step_id: step.id,
      step_number: stepIndex + 1,
      step_count: currentStory.steps.length,
      navigation_method: navigationMethod,
      locale,
    })
    if (trackedStep) {
      const viewedSteps = viewedStoryStepsRef.current.get(currentStory.id) ?? new Set<string>()
      viewedSteps.add(step.id)
      viewedStoryStepsRef.current.set(currentStory.id, viewedSteps)
    }

    const completedSequentially = stepIndex === currentStory.steps.length - 1 && navigationMethod === 'next'
    const viewedEveryStep = currentStory.steps.every((storyStep) => viewedStoryStepsRef.current.get(currentStory.id)?.has(storyStep.id))
    if (completedSequentially && viewedEveryStep && !completedStoriesRef.current.has(currentStory.id)) {
      const tracked = trackEvent('story_complete', {
        story_id: currentStory.id,
        step_count: currentStory.steps.length,
        locale,
      })
      if (tracked) completedStoriesRef.current.add(currentStory.id)
    }
  }, [currentStory, isStoryOverview, locale, stepIndex, storyStep, urlState.mode])

  const visibleGraph = useMemo(() => {
    if (!dataset) return { nodes: [] as GraphNode[], edges: [] as GraphEdge[] }
    let nodes: GraphNode[] = []
    let edges: GraphEdge[] = []

    if (explanationMapEdgeId && selectedEdgeContext?.explanationGraph) {
      const nodeIds = new Set(selectedEdgeContext.explanationGraph.nodeIds)
      const edgeIds = new Set(selectedEdgeContext.explanationGraph.edgeIds)
      nodes = dataset.nodes.filter((node) => nodeIds.has(node.id))
      edges = dataset.edges.filter((edge) => edgeIds.has(edge.id) && nodeIds.has(edge.source) && nodeIds.has(edge.target))
    } else if (urlState.mode === 'story' && currentStory && isStoryOverview && (!urlState.focusId || urlState.lens === 'story')) {
      const overview = getStoryOverviewGraph(dataset, currentStory)
      nodes = overview.nodes
      edges = overview.edges
    } else if (urlState.mode === 'story' && storyStep && (!urlState.focusId || urlState.lens === 'event')) {
      const step = storyStep
      const nodeIds = new Set(step.nodeIds)
      const edgeIds = new Set(step.edgeIds)
      nodes = dataset.nodes.filter((node) => nodeIds.has(node.id))
      edges = dataset.edges.filter((edge) => edgeIds.has(edge.id) && nodeIds.has(edge.source) && nodeIds.has(edge.target))
    } else if (urlState.focusId) {
      const focusEdge = dataset.edges.find((edge) => edge.id === urlState.focusId)
      const baseFocusId = focusEdge?.source ?? urlState.focusId
      const context = urlState.lens === 'all' ? {} : {
        storyId: currentStory?.id ?? urlState.contextId,
        stepId: urlState.lens === 'event' ? storyStep?.id : undefined,
      }
      const neighborhood = getNeighborhood(dataset, baseFocusId, undefined, context)
      const nodeMap = new Map(neighborhood.nodes.map((node) => [node.id, node]))
      const edgeMap = new Map(neighborhood.edges.map((edge) => [edge.id, edge]))
      if (focusEdge) {
        const target = dataset.nodes.find((node) => node.id === focusEdge.target)
        if (target) nodeMap.set(target.id, target)
        edgeMap.set(focusEdge.id, focusEdge)
      }
      expandedIds.forEach((id) => {
        getNeighborhood(dataset, id, NEIGHBOR_EXPANSION_BATCH, context).nodes.forEach((node) => nodeMap.set(node.id, node))
        getNeighborhood(dataset, id, NEIGHBOR_EXPANSION_BATCH, context).edges.forEach((edge) => edgeMap.set(edge.id, edge))
      })
      nodes = [...nodeMap.values()]
      const ids = new Set(nodes.map((node) => node.id))
      edges = [...edgeMap.values()].filter((edge) => ids.has(edge.source) && ids.has(edge.target))
    } else {
      nodes = dataset.nodes
      edges = []
    }

    const preservedNodeIds = new Set<string>()
    if (selectedNode) preservedNodeIds.add(selectedNode.id)
    if (selectedEdge) {
      preservedNodeIds.add(selectedEdge.source)
      preservedNodeIds.add(selectedEdge.target)
    }
    return filterGraph({ nodes, edges }, activeTypes, preservedNodeIds)
  }, [dataset, urlState.mode, urlState.focusId, urlState.lens, urlState.contextId, currentStory, isStoryOverview, storyStep, expandedIds, activeTypes, selectedNode, selectedEdge, selectedEdgeContext, explanationMapEdgeId])

  const storyOverviewPositions = useMemo(() => {
    if (!dataset || !atlasLayout || !currentStory || !isStoryOverview) return undefined
    const storyNodeIds = new Set(getStoryOverviewGraph(dataset, currentStory).nodes.map((node) => node.id))
    return normalizePresetPositions(Object.fromEntries(atlasLayout.placements
      .filter((placement) => storyNodeIds.has(placement.id))
      .map((placement) => [placement.id, { x: placement.x, y: placement.y }])))
  }, [atlasLayout, currentStory, dataset, isStoryOverview])

  const visibleSelectedNodeRelationshipCount = selectedNode
    ? (urlState.mode !== 'story' || urlState.lens === 'all'
      ? new Set(atlasProjection.edges
        .filter((edge) => edge.source === selectedNode.id || edge.target === selectedNode.id)
        .flatMap((edge) => edge.kind === 'bundle' ? edge.edgeIds : [edge.id])).size
      : visibleGraph.edges.filter((edge) => edge.source === selectedNode.id || edge.target === selectedNode.id).length)
    : 0

  const selectNode = useCallback(async (id: string, entryPoint: NodeEntryPoint) => {
    setTimelinePlaying(false)
    await runDetailLoad(() => ensureFocusData(id))
    const node = dataset?.nodes.find((item) => item.id === id)
    trackEvent('node_view', {
      node_id: id,
      node_type: node?.type,
      entry_point: entryPoint,
      experience_mode: urlState.mode === 'story' ? 'story' : 'explore',
      story_id: urlState.mode === 'story' ? urlState.storyId : undefined,
      story_step: urlState.mode === 'story' && storyStep ? stepIndex + 1 : undefined,
      locale,
    })
    setWelcomeDismissed(true)
    setEntryContextId(entryPoint === 'search' ? undefined : selectedNode?.id)
    setSpoilerRevealed(false)
    setFilterOpen(false)
    setReferenceOpen(false)
    setExpandedIds(new Set())
    setSelectedBundle(undefined)
    setExplanationMapEdgeId(undefined)
    setInspectorSnap('peek')
    setInspectorVisible(true)
    setUrlState((state) => state.mode === 'story' && state.storyId
      ? { ...state, focusId: id, contextId: state.lens === 'all' ? undefined : state.storyId }
      : state.timelineId
        ? { ...state, mode: 'explore', focusId: id }
      : { ...state, mode: 'node', focusId: id, storyId: undefined, step: 1, lens: 'all', contextId: undefined })
  }, [dataset, ensureFocusData, locale, runDetailLoad, selectedNode?.id, stepIndex, storyStep, urlState.mode, urlState.storyId])
  const selectGraphNode = useCallback((id: string) => { void selectNode(id, 'graph').catch(() => undefined) }, [selectNode])
  const selectInspectorNode = useCallback((id: string) => { void selectNode(id, 'inspector').catch(() => undefined) }, [selectNode])
  const openCollection = useCallback((id: string, entryPoint: CollectionEntryPoint = 'explorer', resultRank?: number) => {
    void runDetailLoad(async () => {
      const collections = await ensureCollectionData(id)
      const collection = collections.find((item) => item.id === id)
      if (!collection || collection.status !== 'available') throw new Error(`Collection unavailable: ${id}`)
      trackEvent('collection_open', {
        collection_id: id,
        entry_point: entryPoint,
        result_rank: resultRank,
        variant_count: collection.variants.length,
        locale,
      })
      setStoryCatalogOpen(false)
      setReferenceOpen(false)
      setWelcomeDismissed(true)
      setFilterOpen(false)
      setExpandedIds(new Set())
      setSelectedBundle(undefined)
      setExplanationMapEdgeId(undefined)
      setTimelinePlaying(false)
      setInspectorSnap('half')
      setInspectorVisible(true)
      setCollectionCameraRequest((request) => request + 1)
      setUrlState((state) => ({
        ...state,
        mode: 'explore',
        focusId: undefined,
        timelineId: undefined,
        eraId: undefined,
        subEraId: undefined,
        timelineSceneId: undefined,
        timelineTick: 0,
        storyId: undefined,
        storyView: 'chapter',
        step: 1,
        lens: 'all',
        contextId: undefined,
        collectionId: id,
        collectionVariantId: undefined,
      }))
    }).catch(() => undefined)
  }, [ensureCollectionData, locale, runDetailLoad])
  const selectSearchCollection = useCallback((id: string, resultRank: number) => {
    trackEvent('search_select', {
      result_id: id,
      result_type: 'collection',
      result_rank: resultRank,
      experience_mode: urlState.mode === 'story' ? 'story' : 'explore',
      locale,
    })
    openCollection(id, 'search', resultRank)
  }, [locale, openCollection, urlState.mode])
  const toggleReferencePanel = useCallback(() => {
    setStoryCatalogOpen(false)
    setFilterOpen(false)
    setReferenceOpen((open) => !open)
  }, [])
  const openReferenceCollection = useCallback((id: string) => {
    openCollection(id, 'explorer')
  }, [openCollection])
  const toggleFilterPanel = useCallback(() => {
    setStoryCatalogOpen(false)
    setReferenceOpen(false)
    setFilterOpen((open) => !open)
  }, [])
  const selectSearchResult = useCallback((id: string, resultRank: number) => {
    const node = dataset?.nodes.find((item) => item.id === id)
    trackEvent('search_select', {
      result_id: id,
      result_type: node?.type,
      result_rank: resultRank,
      experience_mode: urlState.mode === 'story' ? 'story' : 'explore',
      locale,
    })
    setReferenceOpen(false)
    setUrlState((state) => ({ ...state, collectionId: undefined, collectionVariantId: undefined }))
    if (timelineRoute && isTimelineRequested) {
      const introduction = findTimelineIntroduction(timelineRoute, { kind: 'node', nodeId: id })
      if (introduction) {
        const scene = timelineSceneForPosition(timelineRoute, introduction)
        setTimelinePlaying(false)
        setUrlState((state) => ({
          ...state,
          eraId: introduction.eraId,
          subEraId: introduction.subEraId,
          timelineSceneId: scene?.scene.id,
          timelineTick: scene?.scene.endTick ?? introduction.tick,
        }))
        setToast(locale === 'en' ? 'Opened the first appearance.' : '已跳到首次登場。')
        window.setTimeout(() => setToast(''), 2200)
      } else {
        setToast(locale === 'en' ? 'This figure, place, or event is outside the current timeline. Exit the timeline to explore its full context.' : '這個人物、地點或事件不在目前時間線內；離開時間線後可查看完整脈絡。')
        window.setTimeout(() => setToast(''), 3200)
        return
      }
    }
    void selectNode(id, 'search').catch(() => undefined)
  }, [dataset, isTimelineRequested, locale, selectNode, timelineRoute, urlState.mode])
  const selectEdge = useCallback((id: string) => {
    setTimelinePlaying(false)
    void runDetailLoad(async () => {
      await ensureFocusData(id)
      const edge = dataset?.edges.find((item) => item.id === id)
      trackEvent('relationship_view', {
        relationship_id: id,
        relationship_type: edge?.type,
        entry_point: 'graph',
        experience_mode: urlState.mode === 'story' ? 'story' : 'explore',
        story_id: urlState.mode === 'story' ? urlState.storyId : undefined,
        locale,
      })
      setWelcomeDismissed(true)
      setEntryContextId(undefined)
      setSpoilerRevealed(false)
      setFilterOpen(false)
      setReferenceOpen(false)
      setExpandedIds(new Set())
      setSelectedBundle(undefined)
      setExplanationMapEdgeId(undefined)
      setInspectorSnap('peek')
      setInspectorVisible(true)
      setUrlState((state) => state.mode === 'story' && state.storyId
        ? { ...state, focusId: id, contextId: state.storyId }
        : state.timelineId
          ? { ...state, mode: 'explore', focusId: id }
        : { ...state, mode: 'edge', focusId: id, storyId: undefined, step: 1, lens: 'all', contextId: undefined })
    }).catch(() => undefined)
  }, [dataset, ensureFocusData, locale, runDetailLoad, urlState.mode, urlState.storyId])
  const selectAtlasBundle = useCallback((bundle: AtlasRelationshipBundle) => {
    setSelectedBundle(bundle)
    setInspectorSnap('peek')
    setInspectorVisible(true)
    trackEvent('relationship_bundle_view', {
      focus_id: atlasBaseFocusId,
      relationship_count: bundle.count,
      story_count: bundle.storyIds.length,
      experience_mode: urlState.mode === 'story' ? 'story' : 'explore',
      locale,
    })
  }, [atlasBaseFocusId, locale, urlState.mode])
  const expandNode = useCallback((id: string) => {
    if (expandedIds.has(id)) return
    void runDetailLoad(async () => {
      await ensureFocusData(id)
      const node = dataset?.nodes.find((item) => item.id === id)
      trackEvent('node_expand', {
        node_id: id,
        node_type: node?.type,
        experience_mode: urlState.mode === 'story' ? 'story' : 'explore',
        story_id: urlState.mode === 'story' ? urlState.storyId : undefined,
        expansion_limit: NEIGHBOR_EXPANSION_BATCH,
        locale,
      })
      setExpandedIds((ids) => new Set([...ids, id]))
    }).catch(() => undefined)
  }, [dataset, ensureFocusData, expandedIds, locale, runDetailLoad, urlState.mode, urlState.storyId])
  const resetGraph = useCallback(() => {
    setTimelinePlaying(false)
    if (urlState.mode === 'story') {
      trackEvent('story_exit', {
        story_id: urlState.storyId,
        story_step: storyStep ? stepIndex + 1 : undefined,
        locale,
      })
    }
    setExpandedIds(new Set())
    setEntryContextId(undefined)
    setSelectedBundle(undefined)
    setExplanationMapEdgeId(undefined)
    setSpoilerRevealed(false)
    setReferenceOpen(false)
    setInspectorSnap('peek')
    setInspectorVisible(false)
    setUrlState((state) => ({ ...state, mode: 'explore', focusId: undefined, timelineId: undefined, eraId: undefined, subEraId: undefined, timelineSceneId: undefined, timelineTick: 0, storyId: undefined, storyView: 'chapter', step: 1, lens: 'all', contextId: undefined, collectionId: undefined, collectionVariantId: undefined }))
  }, [locale, stepIndex, storyStep, urlState.mode, urlState.storyId])
  const beginExplore = useCallback(() => {
    trackEvent('explore_start', { locale })
    setWelcomeDismissed(true)
    resetGraph()
  }, [locale, resetGraph])
  const closeInspector = useCallback(() => {
    setExplanationMapEdgeId(undefined)
    setSelectedBundle(undefined)
    if (urlState.mode === 'story' && urlState.focusId) {
      setInspectorSnap('half')
      setInspectorVisible(true)
      setUrlState((state) => ({
        ...state,
        focusId: undefined,
        lens: state.storyView === 'overview' ? 'story' : 'event',
        contextId: state.storyView === 'overview' ? undefined : state.storyId,
      }))
    }
    else if (urlState.timelineId) {
      setInspectorSnap('half')
      setInspectorVisible(true)
      setUrlState((state) => ({ ...state, focusId: undefined }))
    }
    else if (urlState.mode !== 'story') setInspectorVisible(false)
  }, [urlState.mode, urlState.focusId, urlState.timelineId])

  const openStory = useCallback((id: string) => {
    const story = dataset?.stories.find((item) => item.id === id)
    if (!story || !canOpenStoryRecord(story)) return
    void runDetailLoad(async () => {
      await ensureStoryData(id)
      trackEvent('story_open', {
        story_id: id,
        step_count: story.steps.length,
        locale,
      })
      pendingStoryNavigationRef.current = 'story_open'
      lastTrackedStoryStepRef.current = ''
      setStoryCatalogOpen(false)
      setWelcomeDismissed(true)
      setFilterOpen(false)
      setReferenceOpen(false)
      setExplanationMapEdgeId(undefined)
      setSpoilerRevealed(true)
      setInspectorSnap('half')
      setInspectorVisible(true)
      setUrlState((state) => ({ ...state, mode: 'story', focusId: undefined, timelineId: undefined, eraId: undefined, subEraId: undefined, timelineSceneId: undefined, timelineTick: 0, storyId: id, storyView: 'overview', step: 1, lens: 'story', contextId: undefined, collectionId: undefined, collectionVariantId: undefined }))
    }).catch(() => undefined)
  }, [canOpenStoryRecord, dataset, ensureStoryData, locale, runDetailLoad])

  const changeStoryStep = useCallback((index: number, entryPoint: 'reader' | 'progress' = 'reader') => {
    if (!currentStory || index < 0 || index >= currentStory.steps.length || (!isStoryOverview && index === stepIndex)) return
    pendingStoryNavigationRef.current = isStoryOverview
      ? 'story_open'
      : entryPoint === 'progress'
      ? 'progress'
      : index > stepIndex ? 'next' : 'previous'
    setSpoilerRevealed(index < 9)
    setExplanationMapEdgeId(undefined)
    setInspectorVisible(true)
    setUrlState((state) => ({ ...state, storyView: 'chapter', step: index + 1, focusId: undefined, lens: 'event', contextId: state.storyId }))
  }, [currentStory, isStoryOverview, stepIndex])

  const openStoryOverview = useCallback(() => {
    if (!currentStory || isStoryOverview) return
    lastTrackedStoryStepRef.current = ''
    setExpandedIds(new Set())
    setExplanationMapEdgeId(undefined)
    setSpoilerRevealed(true)
    setInspectorSnap('half')
    setInspectorVisible(true)
    setUrlState((state) => ({ ...state, storyView: 'overview', step: 1, focusId: undefined, lens: 'story', contextId: undefined }))
  }, [currentStory, isStoryOverview])

  const changeLens = useCallback((lens: GraphLens) => {
    if (lens === urlState.lens) return
    trackEvent('graph_scope_change', {
      from_scope: urlState.lens,
      to_scope: lens,
      story_id: urlState.storyId,
      node_id: urlState.focusId,
      story_step: storyStep ? stepIndex + 1 : undefined,
      locale,
    })
    setExpandedIds(new Set())
    setUrlState((state) => ({ ...state, lens, contextId: lens === 'all' ? undefined : state.storyId ?? state.contextId }))
  }, [locale, stepIndex, storyStep, urlState.focusId, urlState.lens, urlState.storyId])

  const copyShareLink = async () => {
    const url = window.location.href
    try { await navigator.clipboard.writeText(url) }
    catch {
      const textarea = document.createElement('textarea')
      textarea.value = url
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      textarea.remove()
    }
    trackEvent('share_state', {
      experience_mode: urlState.mode === 'story' ? 'story' : 'explore',
      content_mode: urlState.mode,
      focus_id: urlState.focusId,
      story_id: urlState.storyId,
      story_step: urlState.mode === 'story' && storyStep ? stepIndex + 1 : undefined,
      graph_scope: urlState.mode === 'story' ? urlState.lens : 'all',
      locale,
    })
    setToast(t.shared)
    window.setTimeout(() => setToast(''), 1800)
  }

  const shareGraphImage = async () => {
    if (!graphCanvasRef.current || shareBusy) return
    setShareBusy(true)
    try {
      const title = selectedNode
        ? localize(selectedNode.name, locale)
        : selectedEdge
          ? localize(selectedEdge.label, locale)
          : currentStory && isStoryOverview
          ? localize(currentStory.title, locale)
          : storyStep
            ? localize(storyStep.title, locale)
            : locale === 'en' ? `${activeDatasetShortName} Relationship Map` : `${activeDatasetShortName}關係圖`
      const context = currentStory && isStoryOverview
        ? locale === 'en'
          ? `${localize(currentStory.title, locale)} · Story overview`
          : `${localize(currentStory.title, locale)} · 故事總覽`
        : currentStory && storyStep
        ? locale === 'en'
          ? `${localize(currentStory.title, locale)} · Chapter ${stepIndex + 1}/${currentStory.steps.length}`
          : `${localize(currentStory.title, locale)} · 第 ${stepIndex + 1}/${currentStory.steps.length} 章`
        : selectedNode
          ? locale === 'en' ? 'Across myths' : '跨故事關係'
        : selectedEdge
            ? locale === 'en' ? `Relationship in the ${activeDatasetShortName} atlas` : `${activeDatasetShortName}關係`
            : locale === 'en' ? `${activeDatasetShortName} Atlas` : `${activeDatasetShortName}地圖`
      const filenameSubject = currentStory
        ? isStoryOverview ? `${currentStory.id}-overview` : `${currentStory.id}-chapter-${stepIndex + 1}`
        : selectedNode?.id ?? selectedEdge?.id ?? 'atlas'
      const graph = await graphCanvasRef.current.exportPng()
      const card = await createGraphShareCard({
        graph,
        locale,
        title,
        context,
        nodeCount: visibleGraph.nodes.length,
        edgeCount: visibleGraph.edges.length,
      })
      const result = await shareGraphCard(card, buildGraphShareFilename(filenameSubject), title, window.location.href)
      if (result === 'cancelled') return
      trackEvent('graph_image_share', {
        share_method: result,
        experience_mode: urlState.mode === 'story' ? 'story' : 'explore',
        content_mode: urlState.mode,
        focus_id: urlState.focusId,
        story_id: urlState.storyId,
        story_step: urlState.mode === 'story' && storyStep ? stepIndex + 1 : undefined,
        graph_scope: urlState.mode === 'story' ? urlState.lens : 'all',
        node_count: visibleGraph.nodes.length,
        edge_count: visibleGraph.edges.length,
        locale,
      })
      setToast(result === 'native' ? t.imageShared : t.imageDownloaded)
      window.setTimeout(() => setToast(''), 2200)
    } catch (error) {
      console.error('Graph image sharing failed', error)
      setToast(t.imageShareError)
      window.setTimeout(() => setToast(''), 2600)
    } finally {
      setShareBusy(false)
    }
  }

  const chooseLanguage = (selectedLocale: Locale) => {
    trackEvent('language_change', { from_locale: urlState.locale, to_locale: selectedLocale, entry_point: 'language_gate' })
    setUrlState((state) => ({ ...state, locale: selectedLocale }))
    setLanguageChosen(true)
  }
  const toggleLocale = () => {
    const nextLocale = urlState.locale === 'en' ? 'zh-Hant' : 'en'
    trackEvent('language_change', { from_locale: urlState.locale, to_locale: nextLocale, entry_point: 'header' })
    setUrlState((state) => ({ ...state, locale: nextLocale }))
  }
  const switchDataset = useCallback((datasetId: string) => {
    if (datasetId === urlState.datasetId) return
    const entry = datasetEntries.find((item) => item.id === datasetId)
    if (!entry || entry.status !== 'available') return
    trackEvent('dataset_switch', { from_dataset: urlState.datasetId, to_dataset: datasetId, locale })
    setStoryCatalogOpen(false)
    setReferenceOpen(false)
    setFilterOpen(false)
    setWelcomeDismissed(false)
    setInspectorVisible(false)
    setInspectorSnap('peek')
    setCollectionCameraRequest((request) => request + 1)
    setActiveTypes(new Set(allNodeTypes))
    setHighlightedType(undefined)
    setEntryContextId(undefined)
    setSpoilerRevealed(false)
    setSelectedBundle(undefined)
    setExplanationMapEdgeId(undefined)
    setExpandedIds(new Set())
    setTimelinePlaying(false)
    setUrlState((state) => ({
      ...state,
      datasetId,
      mode: 'explore',
      focusId: undefined,
      timelineId: undefined,
      eraId: undefined,
      subEraId: undefined,
      timelineSceneId: undefined,
      timelineTick: 0,
      storyId: undefined,
      storyView: 'chapter',
      step: 1,
      lens: 'all',
      contextId: undefined,
      collectionId: undefined,
      collectionVariantId: undefined,
    }))
  }, [datasetEntries, locale, urlState.datasetId])
  const selectTradition = useCallback((datasetId: string) => {
    setTraditionSelectorOpen(false)
    setTraditionSelectorRequired(false)
    switchDataset(datasetId)
  }, [switchDataset])
  const toggleType = (type: NodeType) => {
    if (activeTypes.has(type) && activeTypes.size === 1) return
    const enabled = !activeTypes.has(type)
    trackEvent('node_filter_change', {
      node_type: type,
      enabled,
      active_type_count: enabled ? activeTypes.size + 1 : activeTypes.size - 1,
      experience_mode: urlState.mode === 'story' ? 'story' : 'explore',
      locale,
    })
    setActiveTypes((types) => {
      const next = new Set(types)
      if (next.has(type) && next.size > 1) next.delete(type)
      else next.add(type)
      return next
    })
  }
  const highlightType = (type?: NodeType) => {
    if (type === highlightedType) return
    trackEvent('node_type_highlight', {
      node_type: type ?? 'all',
      enabled: Boolean(type),
      experience_mode: urlState.mode === 'story' ? 'story' : 'explore',
      locale,
    })
    if (type) setActiveTypes((types) => types.has(type) ? types : new Set([...types, type]))
    setSelectedBundle(undefined)
    setHighlightedType(type)
  }
  const changeLabelMode = (nextMode: RelationshipLabelMode) => {
    if (nextMode === labelMode) return
    trackEvent('relationship_label_change', { from_mode: labelMode, to_mode: nextMode, locale })
    setLabelMode(nextMode)
  }
  const revealSpoiler = () => {
    trackEvent('spoiler_reveal', {
      content_mode: urlState.mode,
      focus_id: urlState.focusId,
      story_id: urlState.storyId,
      story_step: urlState.mode === 'story' && storyStep ? stepIndex + 1 : undefined,
      locale,
    })
    setSpoilerRevealed(true)
  }
  const viewAllConnections = (id: string) => {
    void runDetailLoad(async () => {
      await ensureFocusData(id)
      trackEvent('view_all_connections', {
        node_id: id,
        from_story_id: urlState.storyId,
        from_story_step: urlState.mode === 'story' && storyStep ? stepIndex + 1 : undefined,
        locale,
      })
      setExpandedIds(new Set())
      setExplanationMapEdgeId(undefined)
      setInspectorSnap('half')
      setInspectorVisible(true)
      setUrlState((state) => ({ ...state, mode: 'node', focusId: id, storyId: undefined, storyView: 'chapter', step: 1, lens: 'all', contextId: undefined }))
    }).catch(() => undefined)
  }
  const viewEdgeInStory = useCallback((storyId: string, stepId: string) => {
    if (!dataset || !selectedEdge) return
    const targetStory = dataset.stories.find((item) => item.id === storyId)
    const targetIndex = targetStory?.steps.findIndex((item) => item.id === stepId) ?? -1
    if (!canOpenStoryRecord(targetStory) || targetIndex < 0) return
    void runDetailLoad(async () => {
      await ensureStoryData(storyId)
      pendingStoryNavigationRef.current = 'direct'
      lastTrackedStoryStepRef.current = ''
      setWelcomeDismissed(true)
      setFilterOpen(false)
      setReferenceOpen(false)
      setExpandedIds(new Set())
      setExplanationMapEdgeId(undefined)
      setInspectorSnap('half')
      setInspectorVisible(true)
      setSpoilerRevealed(targetIndex < 9)
      setUrlState((state) => ({
        ...state,
        mode: 'story',
        focusId: selectedEdge.id,
        storyId,
        storyView: 'chapter',
        step: targetIndex + 1,
        lens: 'event',
        contextId: storyId,
        collectionId: undefined,
        collectionVariantId: undefined,
      }))
    }).catch(() => undefined)
  }, [canOpenStoryRecord, dataset, ensureStoryData, runDetailLoad, selectedEdge])
  const showExplanationMap = useCallback(() => {
    if (!selectedEdgeContext?.explanationGraph || !selectedEdge) return
    setExpandedIds(new Set())
    setExplanationMapEdgeId(selectedEdge.id)
    setInspectorSnap('half')
  }, [selectedEdge, selectedEdgeContext])
  const closeExplanationMap = useCallback(() => setExplanationMapEdgeId(undefined), [])
  const clearAtlasFocus = useCallback(() => {
    setExplanationMapEdgeId(undefined)
    setExpandedIds(new Set())
    setTimelinePlaying(false)
    setInspectorVisible(Boolean(urlState.timelineId || urlState.collectionId))
    setUrlState((state) => state.timelineId
      ? { ...state, mode: 'explore', focusId: undefined }
      : { ...state, mode: 'explore', focusId: undefined, storyId: undefined, storyView: 'chapter', step: 1, lens: 'all', contextId: undefined })
  }, [urlState.collectionId, urlState.timelineId])
  const selectCollectionVariant = useCallback((id: string) => {
    if (!selectedCollection || !selectedCollection.variants.some((variant) => variant.id === id)) return
    void runDetailLoad(async () => {
      await ensureCollectionData(selectedCollection.id, id)
      setCollectionCameraRequest((request) => request + 1)
      setUrlState((state) => ({
        ...state,
        collectionVariantId: id === selectedCollection.defaultVariantId ? undefined : id,
      }))
    }).catch(() => undefined)
  }, [ensureCollectionData, runDetailLoad, selectedCollection])
  const viewCollectionOnAtlas = useCallback(() => {
    if (!selectedCollection || !selectedCollectionVariant) return
    void runDetailLoad(async () => {
      await ensureCollectionData(selectedCollection.id, selectedCollectionVariant.id)
      trackEvent('collection_view_atlas', {
        collection_id: selectedCollection.id,
        variant_id: selectedCollectionVariant.id,
        member_count: selectedCollectionVariant.members.length,
        locale,
      })
      setTimelinePlaying(false)
      setWelcomeDismissed(true)
      setCollectionCameraRequest((request) => request + 1)
    }).catch(() => undefined)
  }, [ensureCollectionData, locale, runDetailLoad, selectedCollection, selectedCollectionVariant])
  const clearCollection = useCallback(() => {
    if (selectedCollection) trackEvent('collection_clear', {
      collection_id: selectedCollection.id,
      variant_id: selectedCollectionVariant?.id,
      locale,
    })
    setCollectionCameraRequest((request) => request + 1)
    setUrlState((state) => ({ ...state, collectionId: undefined, collectionVariantId: undefined }))
    setInspectorVisible(Boolean(selectedNode || selectedEdge || selectedBundle || (urlState.mode === 'story' && currentStory) || (isTimelineRequested && timelineRoute)))
  }, [currentStory, isTimelineRequested, locale, selectedBundle, selectedCollection, selectedCollectionVariant?.id, selectedEdge, selectedNode, timelineRoute, urlState.mode])
  const openStoryCatalog = (entryPoint: 'welcome' | 'header' | 'navigation') => {
    trackEvent('story_catalog_open', {
      entry_point: entryPoint,
      experience_mode: urlState.mode === 'story' ? 'story' : 'explore',
      locale,
    })
    setReferenceOpen(false)
    setFilterOpen(false)
    setStoryCatalogOpen(true)
  }

  const openTimeline = useCallback(() => {
    if (isTimelineRequested) return
    if (!atlas?.timelinePath) {
      setToast(locale === 'en' ? 'This mythology tradition does not have a public timeline yet.' : '這個神話傳統暫時未有公開時間線。')
      window.setTimeout(() => setToast(''), 2600)
      return
    }
    setWelcomeDismissed(true)
    setFilterOpen(false)
    setReferenceOpen(false)
    setSelectedBundle(undefined)
    setExplanationMapEdgeId(undefined)
    setTimelinePlaying(false)
    setInspectorSnap('peek')
    setInspectorVisible(true)
    const activate = (route: TimelineRoute) => {
      const first = firstTimelineScene(route)
      if (!first) return
      const position = timelinePositionForScene(route, first.scene.id)
      setUrlState((state) => ({
        ...state,
        mode: 'explore',
        focusId: undefined,
        timelineId: route.id,
        eraId: position.eraId,
        subEraId: position.subEraId,
        timelineSceneId: first.scene.id,
        timelineTick: position.tick,
        storyId: undefined,
        storyView: 'chapter',
        step: 1,
        lens: 'all',
        contextId: undefined,
        collectionId: undefined,
        collectionVariantId: undefined,
      }))
    }
    if (timelineRoute) {
      activate(timelineRoute)
      return
    }
    void ensureTimelineData().then(activate).catch(() => undefined)
  }, [atlas?.timelinePath, ensureTimelineData, isTimelineRequested, locale, timelineRoute])

  const exitTimeline = useCallback(() => {
    setTimelinePlaying(false)
    setTimelineError('')
    setInspectorVisible(false)
    setUrlState((state) => ({
      ...state,
      mode: 'explore',
      focusId: undefined,
      timelineId: undefined,
      eraId: undefined,
      subEraId: undefined,
      timelineSceneId: undefined,
      timelineTick: 0,
    }))
  }, [])

  const selectTimelineScene = useCallback((sceneId: string) => {
    if (!timelineRoute) return
    const scene = findTimelineScene(timelineRoute, sceneId)
    if (!scene || scene.scene.id !== sceneId) return
    const position = timelinePositionForScene(timelineRoute, sceneId)
    setTimelinePlaying(false)
    setTimelineNarrationCurrentTime(0)
    setInspectorVisible(true)
    setUrlState((state) => ({
      ...state,
      focusId: undefined,
      eraId: position.eraId,
      subEraId: position.subEraId,
      timelineSceneId: sceneId,
      timelineTick: position.tick,
    }))
  }, [timelineRoute])

  const playTimeline = useCallback((scope: TimelinePlaybackScope) => {
    if (!timelineRoute || !timelineHydrated || !activeTimelineScene || timelineLoading || timelineError) return
    let start = activeTimelineScene
    if (!nextTimelineScene(timelineRoute, activeTimelineScene.scene.id, scope)) {
      start = firstTimelineScene(timelineRoute, scope === 'era' ? activeTimelineScene.eraId : undefined) ?? activeTimelineScene
    }
    const position = timelinePositionForScene(timelineRoute, start.scene.id)
    setTimelinePlaybackScope(scope)
    if (start.scene.id !== activeTimelineScene.scene.id) setTimelineNarrationCurrentTime(0)
    setInspectorVisible(true)
    setUrlState((state) => ({
      ...state,
      focusId: undefined,
      eraId: position.eraId,
      subEraId: position.subEraId,
      timelineSceneId: start.scene.id,
      timelineTick: position.tick,
    }))
    setTimelinePlaying(true)
  }, [activeTimelineScene, timelineError, timelineHydrated, timelineLoading, timelineRoute])

  const restartTimeline = useCallback(() => {
    if (!timelineRoute) return
    const start = firstTimelineScene(timelineRoute)
    if (!start) return
    const position = timelinePositionForScene(timelineRoute, start.scene.id)
    setTimelinePlaying(false)
    setTimelineNarrationCurrentTime(0)
    if (timelineNarrationAudioRef.current) timelineNarrationAudioRef.current.currentTime = 0
    setUrlState((state) => ({
      ...state,
      focusId: undefined,
      eraId: position.eraId,
      subEraId: position.subEraId,
      timelineSceneId: start.scene.id,
      timelineTick: position.tick,
    }))
  }, [timelineRoute])

  const openTimelineChapter = useCallback((storyId: string, stepId: string) => {
    const story = dataset?.stories.find((item) => item.id === storyId)
    const index = story?.steps.findIndex((step) => step.id === stepId) ?? -1
    if (!story || index < 0) return
    setTimelinePlaying(false)
    void runDetailLoad(async () => {
      await ensureStoryData(storyId)
      setInspectorVisible(true)
      setInspectorSnap('half')
      setUrlState((state) => ({
        ...state,
        mode: 'story',
        focusId: undefined,
        timelineId: undefined,
        eraId: undefined,
        subEraId: undefined,
        timelineSceneId: undefined,
        timelineTick: 0,
        storyId,
        storyView: 'chapter',
        step: index + 1,
        lens: 'event',
        contextId: storyId,
        collectionId: undefined,
        collectionVariantId: undefined,
      }))
    }).catch(() => undefined)
  }, [dataset, ensureStoryData, runDetailLoad])

  const locateStoryStepInTimeline = useCallback((storyId: string, stepId: string) => {
    if (!atlas?.timelinePath) {
      setToast(locale === 'en' ? 'This chapter is not placed in a public timeline yet.' : '本章暫時未編入公開時間線。')
      window.setTimeout(() => setToast(''), 2600)
      return
    }
    const activate = (route: TimelineRoute) => {
      const proemRedirectSceneId = storyId === 'theogony' && stepId === 'theogony-muses-commission'
        ? 'theogony-theogony-first-powers'
        : undefined
      const scene = proemRedirectSceneId
        ? flattenTimelineScenes(route).find((item) => item.scene.id === proemRedirectSceneId)
        : flattenTimelineScenes(route).find((item) => item.scene.sourceSteps.some((sourceStep) => sourceStep.storyId === storyId && sourceStep.stepId === stepId))
      if (!scene) {
        const routeTitle = localize(route.title, locale)
        setToast(locale === 'en' ? `This chapter is not placed in ${routeTitle}.` : `本章尚未編入${routeTitle}。`)
        window.setTimeout(() => setToast(''), 2600)
        return
      }
      const position = timelinePositionForScene(route, scene.scene.id)
      setTimelinePlaying(false)
      setWelcomeDismissed(true)
      setInspectorVisible(true)
      setInspectorSnap('peek')
      setUrlState((state) => ({
        ...state,
        mode: 'explore',
        focusId: undefined,
        timelineId: route.id,
        eraId: position.eraId,
        subEraId: position.subEraId,
        timelineSceneId: scene.scene.id,
        timelineTick: position.tick,
        storyId: undefined,
        storyView: 'chapter',
        step: 1,
        lens: 'all',
        contextId: undefined,
        collectionId: undefined,
        collectionVariantId: undefined,
      }))
    }
    if (timelineRoute) activate(timelineRoute)
    else void ensureTimelineData().then(activate).catch(() => undefined)
  }, [atlas?.timelinePath, ensureTimelineData, locale, timelineRoute])

  if (!languageChosen) return <LanguageGate onSelect={chooseLanguage} />
  if (loadError) return (
    <main className="state-screen"><div className="brand-mark"><span>M</span></div><h1>{t.error}</h1><p>{loadError}</p><button className="primary-button" onClick={() => fetchData(urlState)}>{t.retry}</button></main>
  )
  if (!dataset || !atlas || !atlasLayout) return <main className="state-screen loading"><div className="orbit"><i /><i /><i /></div><h1>MythWeave</h1><p>{t.loading}</p></main>

  const collectionInspectorActive = Boolean(
    selectedCollection
    && !selectedNode
    && !selectedEdge
    && !selectedBundle
    && urlState.mode !== 'story'
    && !(isTimelineRequested && timelineRoute),
  )
  const inspectorOpen = inspectorVisible && Boolean(selectedNode || selectedEdge || selectedBundle || collectionInspectorActive || (urlState.mode === 'story' && currentStory) || (isTimelineRequested && timelineRoute))
  const isAtlasExperience = urlState.mode !== 'story' && !explanationMapEdgeId
  const isStoryAcrossMyths = urlState.mode === 'story'
    && urlState.lens === 'all'
    && Boolean(selectedNode || selectedEdge)
    && !explanationMapEdgeId
  const usesAtlasSurface = isAtlasExperience || isStoryAcrossMyths
  const relatedTypeResultCount = atlasBaseFocusId && highlightedType
    ? atlasProjection.nodes.filter((node) => node.id !== atlasBaseFocusId && node.type === highlightedType).length
    : undefined
  const relatedTypeStatus = relatedTypeResultCount === undefined
    ? ''
    : relatedTypeResultCount === 0
      ? locale === 'en' ? `No related ${copy[locale].nodeTypes[highlightedType!].toLowerCase()} items` : `沒有相關${copy[locale].nodeTypes[highlightedType!]}`
      : locale === 'en' ? `${relatedTypeResultCount} related ${copy[locale].nodeTypes[highlightedType!].toLowerCase()} items` : `${relatedTypeResultCount} 個相關${copy[locale].nodeTypes[highlightedType!]}`
  const shellStyle = {
    '--tradition-accent': activeDatasetEntry?.accent.accent ?? '#D6A647',
    '--tradition-accent-soft': activeDatasetEntry?.accent.accentSoft ?? '#F0D28C',
    '--tradition-accent-muted': activeDatasetEntry?.accent.accentMuted ?? 'rgba(214, 166, 71, .18)',
    '--tradition-panel-tint': activeDatasetEntry?.accent.panelTint ?? 'rgba(214, 166, 71, .07)',
    ...(inspectorDragHeight === null ? {} : { '--mobile-inspector-drag-height': `${inspectorDragHeight}px` }),
  } as CSSProperties
  const timelineDockVisible = urlState.mode === 'explore' && Boolean(atlas.timelinePath)
  const lensName = locale === 'en'
    ? { event: 'This chapter', story: 'This story', all: 'Across myths' }[urlState.lens]
    : { event: '本章', story: '這個故事', all: '跨神話' }[urlState.lens]
  const storyLensOptions: GraphLens[] = isStoryOverview ? ['story', 'all'] : ['event', 'story', 'all']
  const timelineSceneCount = timelineRoute?.eras.reduce((total, era) => total + era.subEras.reduce((sum, subEra) => sum + subEra.scenes.length, 0), 0) ?? 0
  const timelineRouteTitle = timelineRoute ? localize(timelineRoute.title, locale) : locale === 'en' ? 'Timeline' : '時間線'
  const usesDefaultGreekCopy = activeDatasetEntry?.id === 'greek' || !activeDatasetEntry
  const welcomeTitle = usesDefaultGreekCopy
    ? t.introTitle
    : locale === 'en' ? `How would you like to explore ${activeDatasetName}?` : `你想怎樣探索${activeDatasetName}？`
  const welcomeEyebrow = usesDefaultGreekCopy
    ? t.introEyebrow
    : locale === 'en' ? `Interactive ${activeDatasetShortName} myth atlas` : `互動${activeDatasetShortName}神話地圖`
  const welcomeBody = usesDefaultGreekCopy
    ? t.introBody
    : activeDatasetEntry.description[locale]
  const graphAnnouncement = isTimelineRequested && activeTimelineSubEra && activeTimelineScene
    ? locale === 'en'
      ? `${timelineRouteTitle}, ${localize(activeTimelineScene.scene.title, locale)}, scene ${activeTimelineScene.scene.globalOrder} of ${timelineSceneCount}. ${atlasProjection.nodes.length} revealed items and ${atlasProjection.edges.length} active relationships.`
      : `${timelineRouteTitle}，${localize(activeTimelineScene.scene.title, locale)}，故事章節 ${activeTimelineScene.scene.globalOrder}／${timelineSceneCount}；已揭示 ${atlasProjection.nodes.length} 個項目及 ${atlasProjection.edges.length} 條正在顯示的關係。`
    : isAtlasExperience
    ? selectedCollection && selectedCollectionVariant
      ? locale === 'en'
        ? `Reference list view, ${localize(selectedCollection.title, locale)}, ${selectedCollectionVariant.members.length} highlighted names. ${atlasProjection.nodes.length} visible mythic items and ${atlasProjection.edges.length} visible connections.`
        : `參考名單視圖，${localize(selectedCollection.title, locale)}，標示 ${selectedCollectionVariant.members.length} 個名字。畫面有 ${atlasProjection.nodes.length} 個神話項目和 ${atlasProjection.edges.length} 條關係。`
      : locale === 'en'
        ? `${urlState.focusId ? 'Focused mythology atlas' : 'Fixed mythology atlas'} with ${atlasProjection.nodes.length} visible mythic items and ${atlasProjection.edges.length} visible connections. Zoom to reveal more detail.`
        : `${urlState.focusId ? '已聚焦的神話地圖' : '神話地圖'}，目前顯示 ${atlasProjection.nodes.length} 個神話項目及 ${atlasProjection.edges.length} 項關係。放大可查看更多細節。`
    : locale === 'en'
      ? `${explanationMapEdgeId ? 'Explanation map' : isStoryOverview ? 'Story overview map' : `${lensName} map`}. ${visibleGraph.nodes.length} mythic items and ${visibleGraph.edges.length} relationships.${storyStep ? ` Story step ${stepIndex + 1}: ${localize(storyStep.title, locale)}.` : ''}`
      : `${explanationMapEdgeId ? '關係解說圖' : isStoryOverview ? '故事總覽關係圖' : `${lensName}關係圖`}，目前顯示 ${visibleGraph.nodes.length} 個神話項目及 ${visibleGraph.edges.length} 項關係。${storyStep ? `第 ${stepIndex + 1} 章：${localize(storyStep.title, locale)}。` : ''}`

  return (
    <div
      className={`app-shell ${inspectorOpen ? `has-inspector inspector-snap-${inspectorSnap}` : ''}${inspectorDragHeight === null ? '' : ' is-inspector-dragging'}${explanationMapEdgeId ? ' showing-explanation-map' : ''}${isStoryOverview ? ' story-overview-mode' : ''}${timelineDockVisible ? ' timeline-dock-visible' : ''}${isTimelineRequested ? ' timeline-mode' : ''}`}
      data-tradition={activeDatasetEntry?.id ?? 'greek'}
      style={shellStyle}
    >
      <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">{graphAnnouncement}</p>
      <header className="topbar">
        <button className="brand" onClick={resetGraph}>
          <span className="brand-mark"><span>{brandSymbol}</span></span>
          <span className="brand-words"><strong>MythWeave</strong><small>{brandSubtitle}</small></span>
          <span className="sr-only"> · {t.reset}</span>
        </button>
        <SearchBox
          nodes={dataset.nodes}
          collections={atlas.collections}
          locale={locale}
          onSelect={selectSearchResult}
          onSelectCollection={selectSearchCollection}
        />
        <div className="topbar-controls">
          {datasetEntries.length > 1 && <button
            type="button"
            className="tradition-picker-trigger"
            onClick={() => { setTraditionSelectorRequired(false); setTraditionSelectorOpen(true) }}
            aria-haspopup="dialog"
          >
            <Icon name="spark" size={15} />
            <span>{locale === 'en' ? 'Tradition' : '神話'}</span>
            <strong>{activeDatasetShortName}</strong>
          </button>}
          <nav className="top-actions">
            <a className="library-link" href={publicLibraryHref} aria-label={t.library}><Icon name="book" /><span>{t.library}</span></a>
            <button onClick={() => openStoryCatalog('header')} aria-label={t.stories}><Icon name="myths" /><span>{t.stories}</span></button>
            <button
              className="locale-button"
              onClick={toggleLocale}
              aria-label={locale === 'en'
                ? 'Language: English. Switch to Traditional Chinese / 語言：英文，切換至繁體中文'
                : 'Language: Traditional Chinese. Switch to English / 語言：繁體中文，切換至英文'}
            >{localeName[locale]}</button>
            <ShareMenu locale={locale} busy={shareBusy} onCopyLink={copyShareLink} onShareImage={shareGraphImage} />
            <AnalyticsConsentControl locale={locale} promptEnabled={welcomeDismissed && !traditionSelectorOpen} />
          </nav>
        </div>
      </header>

      <aside className="tool-rail" aria-label="Atlas tools">
        <button className={urlState.mode === 'explore' ? 'active' : ''} onClick={resetGraph}><Icon name="home" /><span>{t.explore}</span></button>
        <button className={urlState.mode === 'story' ? 'active' : ''} onClick={() => openStoryCatalog('navigation')}><Icon name="myths" /><span>{t.stories}</span></button>
        {availableCollectionSummaries.length > 0 && <button
          className={referenceOpen || Boolean(selectedCollection) ? 'active' : ''}
          onClick={toggleReferencePanel}
          aria-label={locale === 'en' ? 'Reference lists' : '參考名單'}
          aria-expanded={referenceOpen}
        >
          <Icon name="list" /><span>{locale === 'en' ? 'Lists' : '名單'}</span>
        </button>}
        <button className={filterOpen ? 'active' : ''} onClick={toggleFilterPanel}><Icon name="filter" /><span>{t.filter}</span></button>
        <div className="rail-thread" />
      </aside>

      <main className="atlas-stage">
        <div className="atmosphere" />
        {usesAtlasSurface ? (
          <AtlasCanvas
            ref={graphCanvasRef}
            nodes={atlasProjection.nodes}
            edges={atlasProjection.edges}
            layout={atlasLayout}
            locale={locale}
            focusId={urlState.focusId ?? activeTimelineTargetId}
            cameraFollow={!isTimelineRequested || Boolean(urlState.focusId) || timelineFollowMap}
            cameraFocusTargets={activeTimelineCameraTargets}
            dimmedNodeIds={timelineDimmedNodeIds}
            collectionHighlightIds={collectionHighlightIds}
            collectionFocusRequest={collectionCameraRequest}
            traditionAccent={activeDatasetEntry?.accent}
            labelMode={labelMode}
            activeTypes={activeTypes}
            highlightedType={highlightedType}
            relatedTypeFilter={atlasBaseFocusId ? highlightedType : undefined}
            loadedRegionIds={loadedRegionIds}
            loadingRegionIds={loadingRegionIds}
            storyContext={isStoryAcrossMyths}
            onRequestRegion={(id) => { void ensureRegion(id).catch(() => undefined) }}
            onSelectNode={selectGraphNode}
            onSelectEdge={selectEdge}
            onSelectBundle={selectAtlasBundle}
            onClearFocus={isStoryAcrossMyths ? closeInspector : clearAtlasFocus}
          />
        ) : (
          <GraphCanvas
            ref={graphCanvasRef}
            nodes={visibleGraph.nodes}
            edges={visibleGraph.edges}
            locale={locale}
            mode={urlState.mode}
            focusId={urlState.focusId}
            labelMode={labelMode}
            contextStoryId={currentStory?.id ?? urlState.contextId}
            contextStepId={storyStep?.id}
            presetPositions={isStoryOverview ? storyOverviewPositions : undefined}
            highlightedType={urlState.mode === 'story' ? highlightedType : undefined}
            onSelectNode={selectGraphNode}
            onSelectEdge={selectEdge}
            onExpandNode={expandNode}
          />
        )}
        {!isTimelineRequested && (
          <div className="graph-legends">
            <NodeLegend
              locale={locale}
              active={activeTypes}
              onToggle={toggleType}
              behavior={usesAtlasSurface && atlasBaseFocusId ? 'related-filter' : isAtlasExperience || urlState.mode === 'story' ? 'highlight' : 'filter'}
              highlightedType={isAtlasExperience || urlState.mode === 'story' ? highlightedType : undefined}
              onHighlight={highlightType}
            />
            {usesAtlasSurface && <RelationshipLegend locale={locale} />}
          </div>
        )}
        {usesAtlasSurface && relatedTypeStatus && <div className={`related-filter-status${relatedTypeResultCount === 0 ? ' empty' : ''}`} role="status" aria-live="polite">
          {relatedTypeStatus}
        </div>}
        {detailLoading && <aside className="atlas-detail-loading" role="status" aria-live="polite">
          <i aria-hidden="true" />{locale === 'en' ? 'Loading related myth content…' : '正在載入相關神話內容…'}
        </aside>}
        {explanationMapEdgeId ? (
          <div className="graph-context-badge explanation-map-badge" data-testid="graph-context"><Icon name="spark" size={15} /><span>{locale === 'en' ? 'Explanation map' : '關係解說圖'}</span></div>
        ) : urlState.mode === 'story' && selectedNode ? (
          <div className="graph-context-control" data-testid="graph-context">
            <span className="graph-context-heading">{t.modeStory} · {t.graphScope}</span>
            <div className="lens-control" role="group" aria-label={t.graphScope}>
              {storyLensOptions.map((lens) => (
                <button key={lens} className={urlState.lens === lens ? 'active' : ''} aria-pressed={urlState.lens === lens} onClick={() => changeLens(lens)}>
                  {{ event: t.scopeChapter, story: t.scopeStory, all: t.scopeAll }[lens]}
                </button>
              ))}
            </div>
          </div>
        ) : selectedCollection && selectedCollectionVariant && !urlState.focusId ? (
          <div className="graph-context-badge collection-context-badge" data-testid="graph-context">
            <Icon name="list" size={15} />
            <span>{locale === 'en'
              ? `${localize(selectedCollection.title, locale)} · ${selectedCollectionVariant.members.length} names`
              : `${localize(selectedCollection.title, locale)} · ${selectedCollectionVariant.members.length} 個名字`}</span>
            {!inspectorOpen && <button type="button" className="graph-context-reopen" onClick={() => setInspectorVisible(true)}>
              {locale === 'en' ? 'View details' : '查看詳情'}
            </button>}
            <button type="button" className="graph-context-reopen" onClick={clearCollection}>
              {locale === 'en' ? 'Clear' : '清除'}
            </button>
          </div>
        ) : (urlState.focusId || urlState.mode === 'story' || welcomeDismissed) && (
          <div className="graph-context-badge" data-testid="graph-context">
            <Icon name={urlState.mode === 'story' ? 'myths' : 'spark'} size={15} />
            <span>{urlState.mode === 'story'
            ? isStoryOverview ? `${t.modeStory} · ${t.storyOverview}` : `${t.modeStory} · ${t.scopeChapter}`
              : welcomeDismissed && !urlState.focusId
                ? locale === 'en'
                  ? `${t.modeExplore} · ${t.scopeAll} · ${activeDatasetShortName} atlas`
                  : `${t.modeExplore} · ${t.scopeAll} · ${activeDatasetShortName}地圖`
                : `${t.modeExplore} · ${t.scopeAll}`}</span>
            {urlState.mode !== 'story' && urlState.focusId && !inspectorOpen && <button type="button" className="graph-context-reopen" onClick={() => setInspectorVisible(true)}>
              {locale === 'en' ? 'View details' : '查看詳情'}
            </button>}
          </div>
        )}
        {urlState.mode === 'explore' && !urlState.focusId && !welcomeDismissed && (
          <section className="welcome-card" aria-labelledby="welcome-title">
            <span className="eyebrow">{welcomeEyebrow}</span>
            <h1 id="welcome-title">{welcomeTitle}</h1>
            <p>{welcomeBody}</p>
            <p className="welcome-coverage" data-testid="landing-coverage">
              <Icon name="search" size={15} />
              <span>{locale === 'en'
                ? <>This {activeDatasetShortName} atlas contains <strong>{dataset.nodes.length} searchable figures, places and events</strong>. Zoom into a region or use search above.</>
                : <>這張{activeDatasetShortName}地圖收錄 <strong>{dataset.nodes.length} 個可搜尋人物、地點和事件</strong>。你可以放大不同區域，或使用上方搜尋。</>}</span>
            </p>
            <div className="welcome-paths">
              <button className="welcome-path" onClick={beginExplore}>
                <span className="welcome-path-icon"><Icon name="spark" /></span>
                <span className="welcome-path-copy"><strong>{t.welcomeExploreTitle}</strong><small>{t.welcomeExploreBody}</small><em>{t.begin}<Icon name="arrow-right" size={15} /></em></span>
              </button>
              <button className="welcome-path" onClick={() => openStoryCatalog('welcome')}>
                <span className="welcome-path-icon"><Icon name="myths" /></span>
                <span className="welcome-path-copy"><strong>{t.welcomeStoryTitle}</strong><small>{t.welcomeStoryBody}</small><em>{t.openStories}<Icon name="arrow-right" size={15} /></em></span>
              </button>
            </div>
          </section>
        )}
        {urlState.mode === 'explore' && atlas.timelinePath && (!timelineRoute || !timelineDockScene) && (
          <TimelineInactiveControls locale={locale} onActivate={openTimeline} />
        )}
        {urlState.mode === 'explore' && atlas.timelinePath && timelineRoute && timelineDockScene && (
          <TimelineControls
            locale={locale}
            route={timelineRoute}
            currentScene={timelineDockScene}
            playing={isTimelineRequested && timelinePlaying}
            speed={timelineSpeed}
            playbackScope={timelinePlaybackScope}
            followMap={timelineFollowMap}
            narrationMode={timelineNarrationMode}
            narrationOptions={timelineNarrationOptions}
            sceneDurationMs={timelineDockSceneDurationMs}
            narrationProgress={activeTimelineNarrationTrack && activeTimelineNarrationKey && timelineNarrationDurations[activeTimelineNarrationKey]
              ? {
                currentTimeSeconds: timelineNarrationCurrentTime,
                durationSeconds: timelineNarrationDurations[activeTimelineNarrationKey],
              }
              : undefined}
            durationMsBySceneId={timelineDurationMsBySceneId}
            loading={timelineLoading && !timelineRoute}
            hydrating={isTimelineRequested && !timelineHydrated && !timelineError}
            error={timelineError || undefined}
            active={isTimelineRequested}
            onSelectScene={selectTimelineScene}
            onPlay={playTimeline}
            onPause={() => setTimelinePlaying(false)}
            onRestart={restartTimeline}
            onSpeed={setTimelineSpeed}
            onFollowMap={setTimelineFollowMap}
            onNarrationMode={setTimelineNarrationMode}
            onNarrationSeek={seekTimelineNarration}
            onRetry={() => { void ensureTimelineData().catch(() => undefined) }}
            onExit={exitTimeline}
            onActivate={openTimeline}
          />
        )}
        <audio
          ref={timelineNarrationAudioRef}
          preload="metadata"
          onLoadedMetadata={handleTimelineNarrationMetadata}
          onTimeUpdate={handleTimelineNarrationTimeUpdate}
          onEnded={handleTimelineNarrationEnded}
          onError={handleTimelineNarrationError}
        />
        {urlState.mode === 'story' && currentStory && (
          <div className={`story-progress${isStoryOverview ? ' is-overview' : ''}`} aria-label={isStoryOverview ? t.storyOverview : `${stepIndex + 1} / ${currentStory.steps.length}`}>
            <div className="story-progress-meta">
              <button type="button" className="story-progress-title" onClick={openStoryOverview} disabled={isStoryOverview} aria-label={isStoryOverview ? t.storyOverview : t.returnToOverview}>
                {!isStoryOverview && <Icon name="arrow-left" size={14} />}
                <span><small>{isStoryOverview ? t.storyOverview : t.returnToOverview}</small>{localize(currentStory.title, locale)}</span>
              </button>
              <strong>{isStoryOverview ? `${currentStory.steps.length} ${t.chapters}` : `${String(stepIndex + 1).padStart(2, '0')} / ${currentStory.steps.length}`}</strong>
              <button className="story-exit" onClick={resetGraph} aria-label={t.exitStory}><Icon name="close" size={14} /><span>{t.exitStory}</span></button>
            </div>
            <div className="progress-track"><i style={{ width: isStoryOverview ? '0%' : `${((stepIndex + 1) / currentStory.steps.length) * 100}%` }} /></div>
            <div className="progress-dots">{currentStory.steps.map((step, index) => <button key={step.id} className={!isStoryOverview && index === stepIndex ? 'active' : !isStoryOverview && index < stepIndex ? 'passed' : ''} onClick={() => changeStoryStep(index, 'progress')} aria-label={`${t.chapter} ${index + 1}: ${localize(step.title, locale)}`} />)}</div>
          </div>
        )}
      </main>

      {referenceOpen && <ReferencePanel
        locale={locale}
        collections={availableCollectionSummaries}
        onClose={() => setReferenceOpen(false)}
        onOpenCollection={openReferenceCollection}
      />}
      {filterOpen && <FilterPanel locale={locale} active={activeTypes} labelMode={labelMode} onLabelMode={changeLabelMode} onToggle={toggleType} onResetTypes={() => setActiveTypes(new Set(allNodeTypes))} onClose={() => setFilterOpen(false)} />}
      {inspectorOpen && collectionInspectorActive && selectedCollection && selectedCollectionVariant ? (
        <CollectionInspector
          locale={locale}
          dataset={dataset}
          collection={selectedCollection}
          variantId={selectedCollectionVariant.id}
          snap={inspectorSnap}
          onSnapChange={setInspectorSnap}
          onDragHeightChange={setInspectorDragHeight}
          onClose={clearCollection}
          onSelectVariant={selectCollectionVariant}
          onSelectNode={selectInspectorNode}
          onOpenStory={openStory}
          onViewOnAtlas={viewCollectionOnAtlas}
        />
      ) : inspectorOpen && (
        <Inspector
          locale={locale}
          dataset={dataset}
          node={selectedNode}
          edge={selectedEdge}
          bundle={selectedBundle}
          entryContextNode={entryContextNode}
          story={urlState.mode === 'story' ? currentStory : undefined}
          storyOverview={isStoryOverview}
          stepIndex={stepIndex}
          spoilerRevealed={spoilerRevealed}
          snap={inspectorSnap}
          onReveal={revealSpoiler}
          onClose={urlState.mode === 'story' && !urlState.focusId ? undefined : closeInspector}
          closeAction={urlState.mode === 'story' ? 'back' : 'close'}
          onSnapChange={setInspectorSnap}
          onDragHeightChange={setInspectorDragHeight}
          onSelectNode={selectInspectorNode}
          onSelectEdge={selectEdge}
          onFilterRelatedType={usesAtlasSurface && atlasBaseFocusId ? highlightType : undefined}
          onStep={(index) => changeStoryStep(index, 'reader')}
          visibleRelationshipCount={visibleSelectedNodeRelationshipCount}
          onViewAll={viewAllConnections}
          onViewInStory={viewEdgeInStory}
          canOpenStory={canOpenStoryRecord}
          onLocateInTimeline={urlState.mode === 'story' && storyStep ? locateStoryStepInTimeline : undefined}
          explanationMapActive={explanationMapEdgeId === selectedEdge?.id}
          onShowExplanationMap={showExplanationMap}
          onCloseExplanationMap={closeExplanationMap}
          timeline={isTimelineRequested && timelineRoute && activeTimelineEra && activeTimelineSubEra && activeTimelineScene && !urlState.focusId ? {
            route: timelineRoute,
            era: activeTimelineEra,
            subEra: activeTimelineSubEra,
            scene: activeTimelineScene.scene,
            sceneIndex: activeTimelineScene.scene.globalOrder,
            onOpenChapter: openTimelineChapter,
          } : undefined}
        />
      )}
      {storyCatalogOpen && <StoryCatalog
        locale={locale}
        cycles={dataset.storyCycles}
        stories={dataset.stories}
        allowPreviewStories={storyPreviewEnabled}
        onClose={() => setStoryCatalogOpen(false)}
        onOpenStory={openStory}
      />}
      {traditionSelectorOpen && datasetEntries.length > 1 && <TraditionSelector
        locale={locale}
        datasets={datasetEntries}
        activeId={urlState.datasetId}
        required={traditionSelectorRequired}
        onSelect={selectTradition}
        onClose={traditionSelectorRequired ? undefined : () => setTraditionSelectorOpen(false)}
      />}
      {toast && <div className="toast" role="status">{toast}</div>}
    </div>
  )
}
